import React, { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import dayjs, { Dayjs } from "dayjs";
import {
  Card,
  DatePicker,
  Typography,
  InputNumber,
  Button,
  message,
  Select,
  Space,
  Row,
  Col,
  Statistic,
  Divider,
  Tag,
  Tooltip,
  Spin,
  List,
  Segmented,
  Input,
  Modal,
} from "antd";
import {
  ArrowUpOutlined,
  ArrowDownOutlined,
  DownOutlined,
  UpOutlined,
  InfoCircleOutlined,
  LeftOutlined,
  RightOutlined,
  PlusOutlined,
  DeleteOutlined,
  SaveOutlined,
  CalendarOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import { BASE_API_URL } from "../../utils/consts";
import { useSelector, useDispatch } from "react-redux";
import { isAdmin, isSuperAdmin } from "../../utils/permissions";
import { getUserWorkLocationsHelper } from "../../utils/helpers/workLocationsHelper";
import { useAuth } from "../../utils/useAuth";
import { startOfMonth, endOfMonth } from "date-fns";
import "./ExpensesMatrix.scss";
import { usePermissions } from "../../utils/usePermissions";
import { useTranslation } from "react-i18next";
import DeductionsDetailsModal from "./DeductionsDetailsModal";
import UnpaidExpensesModal from "../../Components/Modals/UnpaidExpensesModal/UnpaidExpensesModal";
import ManageExtraIncomeModal from "../../Components/Modals/ManageExtraIncomeModal/ManageExtraIncomeModal";
import { isLocationActive } from "../../utils/helpers/locationVisibility";
import {
  getSupplierCustomValues,
  createSupplierCustomValue,
  updateSupplierCustomValue,
  deleteSupplierCustomValue,
  getDailyRevenueSupplierExpenses,
  type SupplierCustomValue,
  type DailyRevenueSupplierExpense,
} from "../../utils/api/supplierExpensesAPI";

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const formatCustomDate = (date: Dayjs | null) =>
  date ? date.format("DD.MM.YYYY") : "";

const parseDateFromName = (name: string): string | undefined => {
  if (!name) return undefined;
  const ddmmyyyy = name.match(/\b(\d{2}\.\d{2}\.\d{4})\b/);
  if (ddmmyyyy) return ddmmyyyy[1];
  const yyyymmdd = name.match(/\b(\d{4}-\d{2}-\d{2})\b/);
  if (yyyymmdd) {
    const [y, m, d] = yyyymmdd[1].split("-");
    return `${d}.${m}.${y}`;
  }
  return undefined;
};

const removeDateFromName = (name: string) =>
  name
    .replace(/\(\s*\d{2}\.\d{2}\.\d{4}\s*\)/g, "")
    .replace(/\b\d{2}\.\d{2}\.\d{4}\b/g, "")
    .replace(/\b\d{4}-\d{2}-\d{2}\b/g, "")
    .replace(/\s+-\s+\d{2}\.\d{2}\.\d{4}/g, "")
    .trim();

const upsertDateIntoName = (name: string, formattedDate: string) => {
  if (!formattedDate) return name;
  const base = removeDateFromName(name);
  if (!base) return formattedDate;
  return `${base} (${formattedDate})`;
};

interface RevenueSummary {
  totalSales: number;
  totalDeductions: number;
  netFromRevenue: number;
  breakdown: {
    cash: number;
    card: number;
    delivery: number;
    bankTransfer: number;
  };
}

const ExpenseMatrixPage: React.FC = () => {
  const dispatch = useDispatch();
  const userDataFromRedux = useSelector((state: any) => state.userData.user);
  const workLocationsState = useSelector((state: any) => state.workLocations);
  const authUser: any = useAuth();

  // Use authUser as fallback if Redux userData is not properly loaded
  const userData = userDataFromRedux?._id ? userDataFromRedux : authUser;
  const clientId = userData?.clientId;
  const { t } = useTranslation();

  // ============================
  //   STATE
  // ============================
  // Matrica je i dalje MESEČNA
  const [month, setMonth] = useState(dayjs().format("YYYY-MM"));
  const [matrix, setMatrix] = useState<any>(null);
  const [originalMatrix, setOriginalMatrix] = useState<any>(null);
  const [categories, setCategories] = useState<any[]>([]); // All categories with suppliers
  const [loading, setLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [supplierSaving, setSupplierSaving] = useState<Record<string, boolean>>(
    {},
  );
  const [loadingRevenue, setLoadingRevenue] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [userWorkLocations, setUserWorkLocations] = useState<any[]>([]);

  // Range picker + navigacija po mesecu (za prihode)
  const [selectedMonth, setSelectedMonth] = useState<Dayjs>(dayjs());
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([
    dayjs(startOfMonth(new Date())),
    dayjs(endOfMonth(new Date())),
  ]);

  // Filteri matrice
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [supplierFilter, setSupplierFilter] = useState<string>("all");

  // Lokacije – za filtriranje dnevnog pazara
  const [locations, setLocations] = useState<any[]>([]);
  const [locationFilters, setLocationFilters] = useState<string[]>([]);
  const scrollContainerRef = React.useRef<HTMLDivElement | null>(null);

  // Finansije (prihodi + odbici iz dnevnog pazara)
  const [income, setIncome] = useState(0);
  const [extraIncomeTotal, setExtraIncomeTotal] = useState(0);
  const [extraIncomeBreakdown, setExtraIncomeBreakdown] = useState<
    Array<{ typeName: string; color: string; count: number; total: number }>
  >([]);
  const [extraIncomeEntries, setExtraIncomeEntries] = useState<
    Array<{
      typeName: string;
      color: string;
      amount: number;
      locationName: string;
      date: string;
    }>
  >([]);
  const [isExtraIncomeModalOpen, setIsExtraIncomeModalOpen] = useState(false);
  const [deductionsFromRevenue, setDeductionsFromRevenue] = useState(0);
  const [summary, setSummary] = useState<RevenueSummary | null>(null);
  const [dailyExpenseAgg, setDailyExpenseAgg] = useState<
    Record<string, { total: number; count: number }>
  >({});
  const [dailyExpenseDetail, setDailyExpenseDetail] = useState<
    Record<
      string,
      {
        total: number;
        count: number;
        dates: Record<string, number>;
        categoryName?: string;
        entries: {
          date: string;
          amount: number;
          location?: string;
          categoryName?: string;
          paymentStatus?: string;
          partialPayment?: {
            paidAmount: number;
            remainingAmount: number;
          };
        }[];
      }
    >
  >({});
  const [dailyRevenueDeductions, setDailyRevenueDeductions] = useState<
    { date: string; description: string; amount: number; location?: string }[]
  >([]);
  const [employeeEarnings, setEmployeeEarnings] = useState<{
    byLocation: Record<
      string,
      {
        locationName: string;
        total: number;
        employees: { name: string; total: number; userId: string }[];
      }
    >;
    bySector: Record<
      string,
      {
        sectorName: string;
        total: number;
        employees: {
          name: string;
          total: number;
          userId: string;
          locationName?: string;
        }[];
      }
    >;
    flat: { user: any; total: number }[];
  }>({ byLocation: {}, bySector: {}, flat: [] });
  const [employeeEarningsLoading, setEmployeeEarningsLoading] = useState(false);
  const [expandedLocations, setExpandedLocations] = useState<
    Record<string, boolean>
  >({});
  const [salaryViewMode, setSalaryViewMode] = useState<"location" | "sector">(
    "location",
  );
  const [customEntryDates, setCustomEntryDates] = useState<
    Record<string, string | undefined>
  >({});
  const [openDateKey, setOpenDateKey] = useState<string | null>(null);
  const [supplierEditLocation, setSupplierEditLocation] = useState<
    Record<string, string | null>
  >({});
  const [collapsedSuppliers, setCollapsedSuppliers] = useState<
    Record<string, boolean>
  >({});
  const [saveAllLoading, setSaveAllLoading] = useState(false);

  // State for partial payment modal (legacy - to be removed)
  const [partialPaymentModal, setPartialPaymentModal] = useState<{
    visible: boolean;
    categoryId: string | null;
    supplierId: string | null;
    locationId: string | null;
    totalAmount: number;
    paidAmount: number;
    remainingAmount: number;
  }>({
    visible: false,
    categoryId: null,
    supplierId: null,
    locationId: null,
    totalAmount: 0,
    paidAmount: 0,
    remainingAmount: 0,
  });

  // State for custom entry partial payment modal
  const [customEntryPartialModal, setCustomEntryPartialModal] = useState<{
    visible: boolean;
    categoryId: string | null;
    supplierId: string | null;
    locationId: string | null;
    entryIndex: number | null;
    totalAmount: number;
    paidAmount: number;
    remainingAmount: number;
  }>({
    visible: false,
    categoryId: null,
    supplierId: null,
    locationId: null,
    entryIndex: null,
    totalAmount: 0,
    paidAmount: 0,
    remainingAmount: 0,
  });

  // State for daily revenue entry payment status modal
  const [dailyRevenuePaymentModal, setDailyRevenuePaymentModal] = useState<{
    visible: boolean;
    entryId: string | null;
    categoryId: string | null;
    supplierId: string | null;
    locationId: string | null;
    date: string | null;
    totalAmount: number;
    currentStatus: string;
    paidAmount: number;
    remainingAmount: number;
  }>({
    visible: false,
    entryId: null,
    categoryId: null,
    supplierId: null,
    locationId: null,
    date: null,
    totalAmount: 0,
    currentStatus: "paid",
    paidAmount: 0,
    remainingAmount: 0,
  });

  // State for deductions details modal
  const [deductionsModalVisible, setDeductionsModalVisible] = useState(false);

  // State for unpaid expenses modal
  const [unpaidExpensesModalVisible, setUnpaidExpensesModalVisible] =
    useState(false);

  // State for new supplier custom values section
  const [customValues, setCustomValues] = useState<SupplierCustomValue[]>([]);
  const [customValuesLoading, setCustomValuesLoading] = useState(false);
  const [dailyRevenueExpenses, setDailyRevenueExpenses] = useState<
    DailyRevenueSupplierExpense[]
  >([]);
  const [customValueModalVisible, setCustomValueModalVisible] = useState(false);
  const [selectedSupplierForCustomValue, setSelectedSupplierForCustomValue] =
    useState<{
      supplierId: string;
      supplierName: string;
      categoryId: string;
      locationId: string;
    } | null>(null);
  const [newCustomValue, setNewCustomValue] = useState<{
    description: string;
    amount: number;
    currency: "RSD" | "EUR";
    month: number;
    year: number;
    invoiceDate: string;
  }>({
    description: "",
    amount: 0,
    currency: "RSD",
    month: dayjs().month() + 1,
    year: dayjs().year(),
    invoiceDate: dayjs().format("YYYY-MM-DD"),
  });

  const locationMap = useMemo<Record<string, string>>(
    () =>
      (locations || []).reduce((acc: Record<string, string>, loc: any) => {
        if (loc?._id) acc[loc._id] = loc.name;
        return acc;
      }, {}),
    [locations],
  );

  // Filter available locations based on user's work locations
  const availableLocations = useMemo(() => {
    // Super admins always see all locations
    if (isSuperAdmin(userData?.roles)) {
      return locations;
    }

    // If user has work locations assigned, show only those
    if (userWorkLocations.length > 0) {
      const filtered = locations.filter((loc: any) =>
        userWorkLocations.some((wl) => wl._id === loc._id),
      );
      return filtered;
    }

    // If no work locations assigned, show all (for admins or users without restriction)
    return locations;
  }, [locations, userWorkLocations, userData?.roles]);

  // Combined loading state - only show full-screen spinner on initial load
  const isPageLoading =
    isInitialLoad &&
    (loading || loadingRevenue || loadingLocations || employeeEarningsLoading);

  // ============================
  //   HELPERS
  // ============================
  const formatProtectedValue = (value: number) => {
    if (isAdmin(userData?.roles) || isSuperAdmin(userData?.roles)) {
      return value.toLocaleString("sr-RS");
    }
    return "***";
  };

  const formatCurrency = (value: number) =>
    `${Number(value || 0).toLocaleString("sr-RS")} RSD`;

  const { hasPermission } = usePermissions();
  const canViewSummary =
    isSuperAdmin(userData?.roles) || hasPermission("business:view-summary");
  const canViewExpenses =
    isSuperAdmin(userData?.roles) || hasPermission("business:view-expenses");
  const canUpdateMatrix =
    isSuperAdmin(userData?.roles) || hasPermission("business:update-expense");
  const canFilterByLocation =
    isSuperAdmin(userData?.roles) ||
    hasPermission("business:filter-by-location");
  const canFilterByCategory =
    isSuperAdmin(userData?.roles) ||
    hasPermission("business:filter-by-category");

  const scrollLeft = () => {
    const current = scrollContainerRef.current;
    if (current) current.scrollLeft -= 150;
  };

  const scrollRight = () => {
    const current = scrollContainerRef.current;
    if (current) current.scrollLeft += 150;
  };

  // ============================
  //   NAVIGACIJA PO MESECIMA
  //   (menja i month i range)
  // ============================
  const handlePrevMonth = () => {
    const newMonth = selectedMonth.subtract(1, "month");
    setSelectedMonth(newMonth);
    setMonth(newMonth.format("YYYY-MM"));

    setDateRange([
      dayjs(startOfMonth(newMonth.toDate())),
      dayjs(endOfMonth(newMonth.toDate())),
    ]);
  };

  const handleNextMonth = () => {
    const newMonth = selectedMonth.add(1, "month");
    setSelectedMonth(newMonth);
    setMonth(newMonth.format("YYYY-MM"));

    setDateRange([
      dayjs(startOfMonth(newMonth.toDate())),
      dayjs(endOfMonth(newMonth.toDate())),
    ]);
  };

  // ============================
  //   LOAD LOKACIJE I USER WORK LOCATIONS
  // ============================
  useEffect(() => {
    if (clientId) {
      setLoadingLocations(true);

      axios
        .get(`${BASE_API_URL}/${clientId}/locations`)
        .then((res) => {
          setLocations((res.data || []).filter(isLocationActive));
        })
        .catch((err) => console.error(err))
        .finally(() => setLoadingLocations(false));
    }
  }, [clientId]);

  // Load user work locations and auto-select them
  useEffect(() => {
    const loadUserWorkLocations = async () => {
      const userId = userData?._id || userData?.id;

      if (!userData?.accessToken || !userId) {
        return;
      }

      try {
        const workLocs = await getUserWorkLocationsHelper(
          userId,
          userData.accessToken,
          dispatch,
          workLocationsState,
        );
        setUserWorkLocations(workLocs);

        // Auto-select all assigned work locations
        // Only for non-super admins who have work locations assigned
        if (!isSuperAdmin(userData?.roles) && workLocs.length > 0) {
          const workLocationIds = workLocs.map((wl: any) => wl._id.toString());
          setLocationFilters(workLocationIds);
        }
      } catch (error) {
        console.error("Error loading user work locations:", error);
      }
    };

    loadUserWorkLocations();
  }, [userData, dispatch, workLocationsState]);

  useEffect(() => {
    if (dateRange[0] && dateRange[1]) {
      fetchEmployeeEarnings(dateRange[0], dateRange[1]);
    }
  }, [dateRange, clientId, locationFilters]);

  const fetchEmployeeEarnings = async (start: Dayjs, end: Dayjs) => {
    if (!clientId) return;
    setEmployeeEarningsLoading(true);
    try {
      const res = await axios.post(`${BASE_API_URL}/salary-summary/bulk`, {
        clientId,
        startDate: start.format("YYYY-MM-DD"),
        endDate: end.format("YYYY-MM-DD"),
      });

      let summaries = res.data?.summaries || [];

      // Ako su izabrane lokacije u headeru, zadrži samo zaposlene sa tih lokacija
      if (locationFilters.length > 0) {
        summaries = summaries.filter((s: any) => {
          const loc = s.user?.location?._id || s.user?.location;
          return loc && locationFilters.includes(loc.toString());
        });
      }

      const byLocation: Record<
        string,
        {
          locationName: string;
          total: number;
          employees: { name: string; total: number; userId: string; isFrozenInPeriod: boolean }[];
        }
      > = {};
      const bySector: Record<
        string,
        {
          sectorName: string;
          total: number;
          employees: {
            name: string;
            total: number;
            userId: string;
            locationName?: string;
            isFrozenInPeriod: boolean;
          }[];
        }
      > = {};

      summaries.forEach(({ user, salarySummary }: any) => {
        // Debug: Log frozen status
        if (user?.isFrozen || user?.isFrozenInPeriod) {
          console.log('🔍 Frozen user detected:', {
            name: `${user?.firstName} ${user?.lastName}`,
            isFrozen: user?.isFrozen,
            isFrozenInPeriod: user?.isFrozenInPeriod,
            frozenAt: user?.frozenAt
          });
        }

        const total = salarySummary?.totalForMonth ?? 0;
        const locId = user?.location?._id || "no-location";
        const locName = user?.location?.name || "Bez lokacije";
        if (!byLocation[locId]) {
          byLocation[locId] = {
            locationName: locName,
            total: 0,
            employees: [],
          };
        }
        byLocation[locId].total += total;
        byLocation[locId].employees.push({
          name:
            `${user?.firstName || ""} ${user?.lastName || ""}`.trim() ||
            "Nepoznat",
          total,
          userId: user?._id,
          isFrozenInPeriod: user?.isFrozenInPeriod || false,
        });

        const sectorId =
          user?.positionInSector?.[0]?.workSector?._id || "no-sector";
        const sectorName =
          user?.positionInSector?.[0]?.workSector?.name || "Bez sektora";
        if (!bySector[sectorId]) {
          bySector[sectorId] = { sectorName, total: 0, employees: [] };
        }
        bySector[sectorId].total += total;
        bySector[sectorId].employees.push({
          name:
            `${user?.firstName || ""} ${user?.lastName || ""}`.trim() ||
            "Nepoznat",
          total,
          userId: user?._id,
          locationName: locName,
          isFrozenInPeriod: user?.isFrozenInPeriod || false,
        });
      });

      setEmployeeEarnings({ byLocation, bySector, flat: summaries });
    } catch (error) {
      console.error(error);
      message.error("Greška pri učitavanju zarada zaposlenih");
    } finally {
      setEmployeeEarningsLoading(false);
    }
  };

  // ============================
  //   LOAD MATRICA (po mesecu)
  // ============================
  const fetchMatrix = async () => {
    if (!clientId || !month) return;
    if (!canViewExpenses) return;
    setLoading(true);

    try {
      const res = await axios.get(`${BASE_API_URL}/business/expense-matrix`, {
        params: { clientId, month },
      });
      setMatrix(res.data);
      setOriginalMatrix(JSON.parse(JSON.stringify(res.data)));
      setIsInitialLoad(false);
    } catch (err) {
      console.error(err);
      message.error(t("Error loading matrix"));
    }

    setLoading(false);
  };

  const fetchCategories = async () => {
    if (!clientId) return;
    try {
      const { data } = await axios.get(
        `${BASE_API_URL}/admin/expense-categories`,
        { params: { clientId, showFrozen: "true" } },
      );
      setCategories(data?.categories || []);
    } catch (err) {
      console.error("Error loading categories:", err);
    }
  };

  // ============================
  //   FETCH EXTRA INCOME
  // ============================
  const fetchExtraIncome = async (start: Dayjs | null, end: Dayjs | null) => {
    if (!clientId || !start || !end) return;

    try {
      // Prepare params with multiple locationIds if selected
      const params: any = {
        clientId,
        startDate: start.format("YYYY-MM-DD"),
        endDate: end.format("YYYY-MM-DD"),
      };

      // If multiple locations selected, send as comma-separated string or array
      if (locationFilters.length > 0) {
        params.locationIds = locationFilters.join(",");
      }

      // Fetch total extra income
      const sumResponse = await axios.get(`${BASE_API_URL}/extra-income/sum`, {
        params,
      });

      // Fetch breakdown by type
      const breakdownResponse = await axios.get(
        `${BASE_API_URL}/extra-income/breakdown`,
        { params },
      );

      // Fetch all entries to show in detailed view with locations
      const entriesResponse = await axios.get(`${BASE_API_URL}/extra-income`, {
        params,
      });

      setExtraIncomeTotal(sumResponse.data.total || 0);
      setExtraIncomeBreakdown(breakdownResponse.data || []);

      // Map entries to simplified structure for display
      const mappedEntries = (entriesResponse.data.entries || []).map(
        (entry: any) => ({
          typeName: entry.typeId?.name || "Nepoznato",
          color: entry.typeId?.color || "#52c41a",
          amount: entry.amount,
          locationName: entry.locationId?.name || "Nepoznata lokacija",
          date: entry.date,
        }),
      );
      setExtraIncomeEntries(mappedEntries);
    } catch (error) {
      console.error("Error fetching extra income:", error);
      setExtraIncomeTotal(0);
      setExtraIncomeBreakdown([]);
      setExtraIncomeEntries([]);
    }
  };

  // ============================
  //   LOAD DAILY REVENUE (range)
  // ============================
  const fetchDailyRevenue = async (start: Dayjs | null, end: Dayjs | null) => {
    if (!clientId || !start || !end) return;
    if (!canViewSummary) return;

    setLoadingRevenue(true);
    try {
      // Prepare params with multiple locationIds if selected
      const params: any = {
        startDate: start.format("YYYY-MM-DD"),
        endDate: end.format("YYYY-MM-DD"),
      };

      if (locationFilters.length > 0) {
        params.locationIds = locationFilters.join(",");
      }

      const { data } = await axios.get(
        `${BASE_API_URL}/admin/${clientId}/getRevenueByRange`,
        { params },
      );

      let revenues = data.revenues ?? [];

      // FILTER PO LOKACIJAMA (client-side fallback ako backend ne podržava locationIds)
      if (locationFilters.length > 0) {
        revenues = revenues.filter((r: any) =>
          locationFilters.includes(r.locationId?._id),
        );
      }

      let totalSales = 0;
      let totalDeductions = 0;

      let cashTotal = 0;
      let cardTotal = 0;
      let deliveryTotal = 0;
      let bankTransferTotal = 0;
      const deductionItems: {
        date: string;
        description: string;
        amount: number;
        location?: string;
      }[] = [];
      const detail: Record<
        string,
        {
          total: number;
          count: number;
          dates: Record<string, number>;
          entries: { date: string; amount: number; location?: string }[];
        }
      > = {};

      revenues.forEach((r: any) => {
        const cash = Number(r.cash || 0) + Number(r.cashAmount || 0);
        const card = Number(r.cardAmount || 0);
        const delivery = Number(r.delivery || 0);
        const bank = Number(r.bankTransfer || 0);
        const drugo = Number(r.drugo || 0);

        cashTotal += cash;
        cardTotal += card;
        deliveryTotal += delivery;
        bankTransferTotal += bank;

        totalSales += cash + card + delivery + bank + drugo;

        if (Array.isArray(r.paymentFromTheRevenue)) {
          r.paymentFromTheRevenue
            .filter((item: any) => !item.other)
            .forEach((item: any) => {
              const amt = Number(item.total || 0);
              if (!amt) return;
              totalDeductions += amt;
              deductionItems.push({
                date: dayjs(r.date).format("DD.MM.YYYY"),
                description: item.description || t("Deductions"),
                amount: amt,
                location: r.locationId?.name,
              });
            });
        }
      });

      setIncome(totalSales);
      setDeductionsFromRevenue(totalDeductions);
      setDailyRevenueDeductions(deductionItems);

      const agg: Record<string, { total: number; count: number }> = {};
      revenues.forEach((r: any) => {
        (r.expenseMatrixEntries || []).forEach((item: any) => {
          if (item?.other) return;

          // IGNORE entries without locationId - ne računamo troškove bez lokacije
          if (!r.locationId) return;

          // Ensure we get string IDs
          const catId =
            item.categoryId?._id?.toString?.() ||
            item.categoryId?.toString?.() ||
            item.categoryId;
          const supId =
            item.supplierId?._id?.toString?.() ||
            item.supplierId?.toString?.() ||
            item.supplierId;
          const locId =
            r.locationId?._id?.toString?.() ||
            r.locationId?.toString?.() ||
            "global";

          const key = `${catId}-${supId}-${locId}`;
          const amount = Number(item.amount || 0);
          const dateKey = dayjs(r.date).format("YYYY-MM-DD");

          console.log("[ExpenseMatrix] Daily Revenue Aggregation:", {
            key,
            catId,
            supId,
            locId,
            amount,
            locationName: r.locationId?.name,
          });

          if (!agg[key]) agg[key] = { total: 0, count: 0 };
          agg[key].total += amount;
          agg[key].count += 1;

          // Extract category name from the item
          const categoryName =
            item.categoryId?.name ||
            item.categoryName ||
            item.description ||
            "-";

          detail[key] = detail[key] || {
            total: 0,
            count: 0,
            dates: {},
            entries: [] as {
              date: string;
              amount: number;
              location?: string;
              categoryName?: string;
            }[],
            categoryName: categoryName,
          };
          detail[key].total += amount;
          detail[key].count += 1;
          detail[key].dates[dateKey] =
            (detail[key].dates[dateKey] || 0) + amount;

          // Try to find payment status from matrix if available
          let paymentStatus = "paid";
          let partialPayment = { paidAmount: 0, remainingAmount: 0 };
          let matrixEntryId = null;

          console.log("[fetchDailyRevenue] Looking for payment status:", {
            catId,
            supId,
            locId,
            dateKey,
            hasMatrix: !!matrix,
            hasRows: matrix?.rows?.length,
          });

          if (matrix && matrix.rows) {
            const row = matrix.rows.find((r: any) => r.categoryId === catId);
            console.log("[fetchDailyRevenue] Found row:", !!row);
            if (row) {
              const supplier = row.suppliers?.find(
                (s: any) =>
                  s.supplierId === supId &&
                  (s.locationId?.toString() || null) ===
                    (locId === "global" ? null : locId),
              );
              console.log("[fetchDailyRevenue] Found supplier:", {
                found: !!supplier,
                hasDailyRevenueEntries: !!supplier?.dailyRevenueEntries,
                entriesCount: supplier?.dailyRevenueEntries?.length,
              });
              if (supplier && supplier.dailyRevenueEntries) {
                const entry = supplier.dailyRevenueEntries.find((e: any) => {
                  const entryDate = dayjs(e.date).format("YYYY-MM-DD");
                  const dateMatches = entryDate === dateKey;
                  // Match by both date and amount to distinguish multiple entries on same date
                  const amountMatches =
                    Math.abs((e.amount || 0) - amount) < 0.01;
                  return dateMatches && amountMatches;
                });
                console.log("[fetchDailyRevenue] Found daily revenue entry:", {
                  found: !!entry,
                  dateKey,
                  amount,
                  entryId: entry?._id,
                  paymentStatus: entry?.paymentStatus,
                });
                if (entry) {
                  paymentStatus = entry.paymentStatus || "unpaid";
                  partialPayment = entry.partialPayment || {
                    paidAmount: 0,
                    remainingAmount: 0,
                  };
                  matrixEntryId = entry._id || null;
                }
              }
            }
          }

          (detail[key].entries as any[]).push({
            date: dateKey,
            amount,
            location: r.locationId?.name,
            categoryName: categoryName,
            paymentStatus: paymentStatus,
            partialPayment: partialPayment,
            entryId: matrixEntryId,
          });
        });
      });

      console.log("[ExpenseMatrix] Daily Expense Aggregation Result:", agg);

      setDailyExpenseAgg(agg);
      setDailyExpenseDetail(detail);

      setSummary({
        totalSales,
        totalDeductions,
        netFromRevenue: totalSales - totalDeductions,
        breakdown: {
          cash: cashTotal,
          card: cardTotal,
          delivery: deliveryTotal,
          bankTransfer: bankTransferTotal,
        },
      });

      // Fetch extra income
      await fetchExtraIncome(start, end);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingRevenue(false);
    }
  };

  // Fetch supplier custom values for the selected month
  const fetchCustomValues = async () => {
    if (!clientId) return;
    setCustomValuesLoading(true);
    try {
      const [year, monthNum] = month.split("-").map(Number);
      const response = await getSupplierCustomValues({
        clientId,
        month: monthNum,
        year,
      });
      console.log("Custom values response:", response.data);
      console.log("First item invoiceDate:", response.data?.[0]?.invoiceDate);
      console.log(
        "First item keys:",
        response.data?.[0] ? Object.keys(response.data[0]) : [],
      );
      setCustomValues(response.data || []);
    } catch (err) {
      console.error("Error fetching custom values:", err);
      message.error(t("Failed to load custom values"));
    } finally {
      setCustomValuesLoading(false);
    }
  };

  // Fetch daily revenue supplier expenses for the selected month
  const fetchDailyRevenueExpenses = async () => {
    if (!clientId) return;
    try {
      const [year, monthNum] = month.split("-").map(Number);
      const startDate = dayjs(
        `${year}-${monthNum.toString().padStart(2, "0")}-01`,
      )
        .startOf("month")
        .format("YYYY-MM-DD");
      const endDate = dayjs(
        `${year}-${monthNum.toString().padStart(2, "0")}-01`,
      )
        .endOf("month")
        .format("YYYY-MM-DD");

      const response = await getDailyRevenueSupplierExpenses({
        clientId,
        startDate,
        endDate,
      });
      setDailyRevenueExpenses(response.data || []);
    } catch (err) {
      console.error("Error fetching daily revenue expenses:", err);
    }
  };

  // Handle adding new custom value
  const handleAddCustomValue = async () => {
    if (!selectedSupplierForCustomValue || !clientId) return;
    if (newCustomValue.amount <= 0) {
      message.warning(t("Amount must be greater than 0"));
      return;
    }

    try {
      await createSupplierCustomValue({
        clientId,
        supplierId: selectedSupplierForCustomValue.supplierId,
        supplierName: selectedSupplierForCustomValue.supplierName,
        locationId: selectedSupplierForCustomValue.locationId,
        month: newCustomValue.month,
        year: newCustomValue.year,
        invoiceDate: newCustomValue.invoiceDate,
        description: newCustomValue.description,
        amount: newCustomValue.amount,
        currency: newCustomValue.currency,
        paymentStatus: "unpaid",
      });

      message.success(t("Custom value added successfully"));
      setCustomValueModalVisible(false);
      setNewCustomValue({
        description: "",
        amount: 0,
        currency: "RSD",
        month: dayjs().month() + 1,
        year: dayjs().year(),
        invoiceDate: dayjs().format("YYYY-MM-DD"),
      });
      setSelectedSupplierForCustomValue(null);
      await fetchCustomValues();
    } catch (err) {
      console.error("Error adding custom value:", err);
      message.error(t("Failed to add custom value"));
    }
  };

  // Handle deleting custom value
  const handleDeleteCustomValue = async (id: string) => {
    try {
      await deleteSupplierCustomValue(id);
      message.success(t("Custom value deleted"));
      await fetchCustomValues();
    } catch (err) {
      console.error("Error deleting custom value:", err);
      message.error(t("Failed to delete custom value"));
    }
  };

  // ============================
  //   EFFECTS
  // ============================
  useEffect(() => {
    if (!clientId) return;
    fetchCategories();
  }, [clientId]);

  useEffect(() => {
    if (!clientId) return;
    if (!canViewExpenses) return;
    fetchMatrix();
  }, [clientId, month, canViewExpenses]);

  useEffect(() => {
    if (!clientId) return;
    if (!canViewSummary) return;
    if (!matrix) return; // Wait for matrix to load first
    fetchDailyRevenue(dateRange[0], dateRange[1]);
  }, [clientId, dateRange, locationFilters, canViewSummary, matrix]);

  // Fetch custom values when month changes
  useEffect(() => {
    if (!clientId) return;
    if (!canViewExpenses) return;
    fetchCustomValues();
    fetchDailyRevenueExpenses();
  }, [clientId, month, canViewExpenses]);

  // ============================
  //   UPDATE CELL VALUE
  // ============================
  const updateValue = (
    categoryId: string,
    supplierId: string,
    locationId: string | null,
    customEntries: { description: string; amount: number }[],
  ) => {
    if (!canUpdateMatrix) return;
    const customValue = customEntries.reduce(
      (sum, ce) => sum + Number(ce.amount || 0),
      0,
    );
    setMatrix((prev: any) => ({
      ...prev,
      rows: prev.rows.map((row: any) => {
        if (row.categoryId !== categoryId) return row;

        const updatedSuppliers = [...row.suppliers];
        const idx = updatedSuppliers.findIndex(
          (s) =>
            s.supplierId === supplierId &&
            (s.locationId || null) === (locationId || null),
        );

        if (idx >= 0) {
          const s = updatedSuppliers[idx];
          const newTotalValue = customValue + Number(s.defaultValue || 0);

          // If supplier has partial payment status, adjust remaining amount
          let updatedPartialPayment = s.partialPayment;
          if (s.paymentStatus === "partial" && s.partialPayment) {
            const paidAmount = s.partialPayment.paidAmount || 0;
            const newRemainingAmount = newTotalValue - paidAmount;
            updatedPartialPayment = {
              paidAmount,
              remainingAmount: Math.max(0, newRemainingAmount),
            };
          }

          updatedSuppliers[idx] = {
            ...s,
            locationId: locationId || null,
            customEntries,
            customValue,
            value: customValue,
            totalValue: newTotalValue,
            partialPayment: updatedPartialPayment,
          };
        } else {
          // Kreiraj novi zapis za lokaciju sa baseline defaultom iz bilo kog postojećeg zapisa tog dobavljača
          const baseline = row.suppliers.find(
            (s: any) => s.supplierId === supplierId,
          );
          const defaultValue = Number(baseline?.defaultValue || 0);
          updatedSuppliers.push({
            supplierId,
            supplierName: baseline?.supplierName || "",
            locationId: locationId || null,
            customEntries,
            customValue,
            value: customValue,
            defaultValue,
            totalValue: defaultValue + customValue,
            excludeFromBusiness: baseline?.excludeFromBusiness || false,
            paymentStatus: baseline?.paymentStatus || "unpaid",
            partialPayment: baseline?.partialPayment || {
              paidAmount: 0,
              remainingAmount: 0,
            },
          });
        }

        return { ...row, suppliers: updatedSuppliers };
      }),
    }));
  };

  // ============================
  //   SAVE HELPERS (MATRIX → DB)
  // ============================
  const normalizeId = (id: any) => {
    if (id === null || id === undefined) return null;
    if (typeof id === "object" && id?._id) return id._id.toString();
    if (typeof id === "string") return id;
    if (typeof id?.toString === "function") return id.toString();
    return null;
  };

  const supplierKey = (categoryId: any, supplierId: any, locationId: any) =>
    `${normalizeId(categoryId)}-${normalizeId(supplierId)}-${normalizeId(
      locationId,
    )}`;

  const originalLookup = useMemo(() => {
    const map = new Map<string, any>();

    (originalMatrix?.rows || []).forEach((row: any) => {
      (row.suppliers || []).forEach((sup: any) => {
        map.set(
          supplierKey(row.categoryId, sup.supplierId, sup.locationId),
          JSON.parse(
            JSON.stringify({
              ...sup,
              customEntries: Array.isArray(sup.customEntries)
                ? sup.customEntries.map((ce: any) => ({
                    description: ce.description || "",
                    amount: Number(ce.amount) || 0,
                  }))
                : [],
            }),
          ),
        );
      });
    });

    return map;
  }, [originalMatrix]);

  const buildSupplierPayload = (
    categoryId: string,
    sup: any,
  ): { categoryId: string | null; supplier: any } | null => {
    const customEntries = Array.isArray(sup.customEntries)
      ? sup.customEntries
          .map((ce: any) => ({
            description: ce.description || "",
            amount: Number(ce.amount) || 0,
            paymentStatus: ce.paymentStatus || "unpaid",
            partialPayment: ce.partialPayment || {
              paidAmount: 0,
              remainingAmount: 0,
            },
          }))
          .filter((ce: any) => ce.amount || ce.description)
      : [];

    const customValue =
      Number(sup.customValue ?? sup.value ?? 0) ||
      customEntries.reduce(
        (sum: number, ce: any) => sum + (Number(ce.amount) || 0),
        0,
      );

    const original = originalLookup.get(
      supplierKey(categoryId, sup.supplierId, sup.locationId),
    );

    const originalEntries = original?.customEntries || [];
    const originalCustom =
      Number(original?.customValue ?? original?.value ?? 0) ||
      originalEntries.reduce(
        (sum: number, ce: any) => sum + (Number(ce.amount) || 0),
        0,
      );
    const originalExcluded = !!original?.excludeFromBusiness;

    const entriesChanged =
      customEntries.length !== originalEntries.length ||
      customEntries.some((ce: any, idx: number) => {
        const orig = originalEntries[idx];
        if (!orig) return true;
        const amountChanged = ce.amount !== (Number(orig.amount) || 0);
        const descChanged = (ce.description || "") !== (orig.description || "");
        const statusChanged =
          (ce.paymentStatus || "unpaid") !== (orig.paymentStatus || "unpaid");
        const partialChanged =
          (Number(ce.partialPayment?.paidAmount) || 0) !==
            (Number(orig.partialPayment?.paidAmount) || 0) ||
          (Number(ce.partialPayment?.remainingAmount) || 0) !==
            (Number(orig.partialPayment?.remainingAmount) || 0);
        return amountChanged || descChanged || statusChanged || partialChanged;
      });

    const include =
      customValue !== originalCustom ||
      entriesChanged ||
      !!sup.excludeFromBusiness !== originalExcluded ||
      !original;

    if (!include) return null;

    const payload: any = {
      supplierId: normalizeId(sup.supplierId),
      locationId: normalizeId(sup.locationId),
      customEntries,
      customValue,
    };

    if (sup.excludeFromBusiness) {
      payload.excludeFromBusiness = true;
    }

    return {
      categoryId: normalizeId(categoryId),
      supplier: payload,
    };
  };

  const collectChangesByCategory = () => {
    const changesByCategory: Record<string, any[]> = {};

    (matrix?.rows || []).forEach((row: any) => {
      (row.suppliers || []).forEach((sup: any) => {
        const payload = buildSupplierPayload(row.categoryId, sup);
        if (payload?.categoryId && payload?.supplier) {
          const catId = payload.categoryId.toString();
          if (!changesByCategory[catId]) changesByCategory[catId] = [];
          changesByCategory[catId].push(payload.supplier);
        }
      });
    });

    const totalChanges = Object.values(changesByCategory).reduce(
      (sum, list) => sum + list.length,
      0,
    );

    return { changesByCategory, totalChanges };
  };

  const hasSupplierChanges = (
    categoryId: string,
    supplierId: string,
    locationId: string | null,
  ) => {
    if (!matrix?.rows) return false;

    const row = matrix.rows.find((r: any) => r.categoryId === categoryId);
    if (!row) return false;

    const supplier = row.suppliers.find(
      (s: any) =>
        normalizeId(s.supplierId) === normalizeId(supplierId) &&
        normalizeId(s.locationId) === normalizeId(locationId),
    );

    if (!supplier) return false;

    return !!buildSupplierPayload(categoryId, supplier);
  };

  const saveSupplier = async (
    categoryId: string,
    supplierId: string,
    locationId: string | null,
  ) => {
    if (!canUpdateMatrix) {
      message.warning(t("No permission to update expense matrix"));
      return;
    }

    if (!matrix || !clientId || !month) {
      message.error(t("Missing data to save"));
      return;
    }

    // Find the specific supplier to save
    const row = matrix.rows.find((r: any) => r.categoryId === categoryId);
    if (!row) {
      message.error(t("Category not found"));
      return;
    }

    const supplier = row.suppliers.find(
      (s: any) =>
        normalizeId(s.supplierId) === normalizeId(supplierId) &&
        normalizeId(s.locationId) === normalizeId(locationId),
    );

    if (!supplier) {
      message.error(t("Supplier not found"));
      return;
    }

    // Build payload for this specific supplier only
    const payload = buildSupplierPayload(categoryId, supplier);

    if (!payload?.categoryId || !payload?.supplier) {
      message.info(t("No changes to save"));
      return;
    }

    const key = supplierKey(categoryId, supplierId, locationId);
    setSupplierSaving((prev) => ({ ...prev, [key]: true }));

    try {
      // Save only this specific supplier
      await axios.put(`${BASE_API_URL}/business/expense-matrix/category`, {
        clientId,
        month,
        categoryId: payload.categoryId,
        suppliers: [payload.supplier], // Only one supplier
      });

      message.success(t("Saved successfully"));
      await fetchMatrix();
    } catch (err) {
      console.error(err);
      message.error(t("Error while saving"));
    } finally {
      setSupplierSaving((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const saveAllChanges = async () => {
    if (!canUpdateMatrix) {
      message.warning(t("No permission to update expense matrix"));
      return;
    }

    if (!matrix || !clientId || !month) {
      message.error(t("Missing data to save"));
      return;
    }

    const { changesByCategory, totalChanges } = collectChangesByCategory();

    if (!totalChanges) {
      message.info(t("No changes to save"));
      return;
    }

    setSaveAllLoading(true);
    try {
      // Save sequentially to avoid version conflicts
      for (const [categoryId, suppliers] of Object.entries(changesByCategory)) {
        await axios.put(`${BASE_API_URL}/business/expense-matrix/category`, {
          clientId,
          month,
          categoryId,
          suppliers,
        });
      }
      message.success(t("Saved successfully"));
      await fetchMatrix();
    } catch (err) {
      console.error(err);
      message.error(t("Error while saving"));
    } finally {
      setSaveAllLoading(false);
    }
  };

  // ============================
  //   RASHODI IZ MATRICE (sa daily revenue dodatim u default)
  // ============================

  // Helper function to create consistent keys for dailyExpenseAgg
  const createDailyExpenseKey = useCallback(
    (categoryId: string, supplierId: any, locationId: any) => {
      // Normalize location ID
      let locKey = "global";
      if (locationId) {
        if (typeof locationId === "object" && (locationId as any)._id) {
          locKey = (locationId as any)._id.toString();
        } else {
          locKey = locationId.toString();
        }
      }

      // Normalize supplier ID
      const supKey =
        typeof supplierId === "object" && (supplierId as any)._id
          ? (supplierId as any)._id.toString()
          : supplierId?.toString() || supplierId;

      return `${categoryId}-${supKey}-${locKey}`;
    },
    [],
  );

  const getDailyAdded = useCallback(
    (rowId: string, sup: any) => {
      const key = createDailyExpenseKey(rowId, sup.supplierId, sup.locationId);
      const result = dailyExpenseAgg[key]?.total || 0;

      if (result > 0) {
        // console.log("[ExpenseMatrix] getDailyAdded:", {
        //   key,
        //   categoryId: rowId,
        //   supplierId: sup.supplierId,
        //   locationId: sup.locationId,
        //   supplierName: sup.supplierName,
        //   result,
        //   availableKeys: Object.keys(dailyExpenseAgg),
        // });
      }

      return result;
    },
    [dailyExpenseAgg, createDailyExpenseKey],
  );

  const getDisplayedDefault = useCallback((rowId: string, sup: any) => {
    // Default value does NOT include daily revenue - those are tracked separately
    const def = Number(sup.defaultValue || 0);
    return def;
  }, []);

  const getDisplayedTotal = useCallback((rowId: string, sup: any) => {
    // Calculate total from individual components to ensure consistency
    // Don't trust backend totalValue as it may be stale after schema changes

    const defaultValue = Number(sup.defaultValue) || 0;

    const customEntriesSum = Array.isArray(sup.customEntries)
      ? sup.customEntries.reduce(
          (sum: number, ce: any) => sum + Number(ce.amount || 0),
          0,
        )
      : 0;

    const dailyRevenueSum = Array.isArray(sup.dailyRevenueEntries)
      ? sup.dailyRevenueEntries.reduce(
          (sum: number, entry: any) => sum + Number(entry.amount || 0),
          0,
        )
      : 0;

    const total = defaultValue + customEntriesSum + dailyRevenueSum;

    // Debug logging for specific suppliers with dailyRevenue
    if (dailyRevenueSum > 0) {
      console.log("[ExpensesMatrix getDisplayedTotal]", {
        supplierId: sup.supplierId,
        supplierName: sup.supplierName,
        locationId: sup.locationId,
        defaultValue,
        customEntriesSum,
        dailyRevenueSum,
        total,
      });
    }

    return total;
  }, []);

  const getDisplayedCustom = useCallback((rowId: string, sup: any) => {
    const customEntriesSum = Array.isArray(sup.customEntries)
      ? sup.customEntries.reduce(
          (sum: number, ce: any) => sum + Number(ce.amount || 0),
          0,
        )
      : 0;
    if (customEntriesSum > 0) return customEntriesSum;
    const raw = Number(sup.customValue ?? sup.value ?? 0);
    return raw > 0 ? raw : undefined;
  }, []);

  const externalExpensesFromMatrix = useMemo(() => {
    if (!matrix || !Array.isArray(matrix.rows)) return 0;
    const isGlobal = !locationFilters.length;
    const activeLocs = !isGlobal ? locationFilters : [];

    const total = matrix.rows.reduce((sum: number, row: any) => {
      const grouped: Record<string, any[]> = {};
      // IGNORE suppliers without locationId - ne računamo troškove bez lokacije
      (row.suppliers || []).forEach((sup: any) => {
        if (!sup.locationId) return; // Skip entries without location
        if (!grouped[sup.supplierId]) grouped[sup.supplierId] = [];
        grouped[sup.supplierId].push(sup);
      });

      const rowTotal = Object.values(grouped).reduce(
        (subSum: number, entries: any[]) => {
          // Ako su lokacije izabrane: uzmi samo te lokacije
          if (activeLocs.length > 0) {
            const locEntries = entries.filter((e) =>
              activeLocs.includes((e.locationId || "").toString()),
            );
            if (locEntries.length > 0) {
              return (
                subSum +
                locEntries.reduce(
                  (s, sup) => s + getDisplayedTotal(row.categoryId, sup),
                  0,
                )
              );
            }
            return subSum;
          }

          // Global prikaz: saberi SVE unose (i globalne i lokacijske)
          return (
            subSum +
            entries.reduce(
              (s, sup) => s + getDisplayedTotal(row.categoryId, sup),
              0,
            )
          );
        },
        0,
      );

      return sum + rowTotal;
    }, 0);

    // Debug logging to compare with Finance
    console.log("[ExpensesMatrix externalExpensesFromMatrix]", {
      total,
      locationFilters,
      matrixRowsCount: matrix?.rows?.length || 0,
    });

    return total;
  }, [matrix, getDisplayedTotal, locationFilters]);

  // Calculate unpaid expenses (unpaid + remaining from partial payments)
  const unpaidExpenses = useMemo(() => {
    if (!matrix || !Array.isArray(matrix.rows)) return 0;
    const isGlobal = !locationFilters.length;
    const activeLocs = !isGlobal ? locationFilters : [];

    return matrix.rows.reduce((sum: number, row: any) => {
      const grouped: Record<string, any[]> = {};
      // IGNORE suppliers without locationId - ne računamo troškove bez lokacije
      (row.suppliers || []).forEach((sup: any) => {
        if (!sup.locationId) return; // Skip entries without location
        if (!grouped[sup.supplierId]) grouped[sup.supplierId] = [];
        grouped[sup.supplierId].push(sup);
      });

      const rowTotal = Object.values(grouped).reduce(
        (subSum: number, entries: any[]) => {
          let chosen: any[] = entries;
          if (activeLocs.length > 0) {
            // SPECIFIC LOCATIONS: Include ONLY entries for those locations
            const locEntries = entries.filter((e) =>
              activeLocs.includes((e.locationId || "").toString()),
            );
            chosen = locEntries;
          } else {
            // GLOBAL: uključi SVE unose (i globalne i lokacijske)
            chosen = entries;
          }

          return (
            subSum +
            chosen.reduce((s: number, sup: any) => {
              let supplierUnpaid = 0;

              // Default value - check payment status
              const defaultValue = Number(sup.defaultValue) || 0;
              const defaultStatus = sup.defaultPaymentStatus || "unpaid";

              if (defaultStatus === "unpaid") {
                supplierUnpaid += defaultValue;
              } else if (
                defaultStatus === "partial" &&
                sup.defaultPartialPayment &&
                defaultValue > 0 // Only count partialPayment if there's actual baseline
              ) {
                const remaining =
                  sup.defaultPartialPayment.remainingAmount || 0;
                supplierUnpaid += remaining;
              }

              // Custom entries - check individual payment status
              if (sup.customEntries && Array.isArray(sup.customEntries)) {
                sup.customEntries.forEach((entry: any) => {
                  const entryStatus = entry.paymentStatus || "unpaid";
                  const entryAmount = Number(entry.amount) || 0;

                  if (entryStatus === "unpaid") {
                    supplierUnpaid += entryAmount;
                  } else if (
                    entryStatus === "partial" &&
                    entry.partialPayment
                  ) {
                    const remaining = entry.partialPayment.remainingAmount || 0;
                    supplierUnpaid += remaining;
                  }
                });
              }

              // Daily revenue entries - defaultValue no longer includes them, count separately
              if (
                sup.dailyRevenueEntries &&
                Array.isArray(sup.dailyRevenueEntries)
              ) {
                sup.dailyRevenueEntries.forEach((entry: any) => {
                  const entryStatus = entry.paymentStatus || "paid"; // Default is PAID for daily revenue
                  const entryAmount = Number(entry.amount) || 0;

                  if (entryStatus === "unpaid") {
                    supplierUnpaid += entryAmount;
                  } else if (
                    entryStatus === "partial" &&
                    entry.partialPayment
                  ) {
                    const remaining = entry.partialPayment.remainingAmount || 0;
                    supplierUnpaid += remaining;
                  }
                });
              }

              return s + supplierUnpaid;
            }, 0)
          );
        },
        0,
      );

      return sum + rowTotal;
    }, 0);
  }, [matrix, locationFilters]);

  // Calculate paid expenses (fully paid + paid portion from partial payments)
  const paidExpenses = useMemo(() => {
    if (!matrix || !Array.isArray(matrix.rows)) return 0;
    const isGlobal = !locationFilters.length;
    const activeLocs = !isGlobal ? locationFilters : [];

    return matrix.rows.reduce((sum: number, row: any) => {
      const grouped: Record<string, any[]> = {};
      // IGNORE suppliers without locationId - ne računamo troškove bez lokacije
      (row.suppliers || []).forEach((sup: any) => {
        if (!sup.locationId) return; // Skip entries without location
        if (!grouped[sup.supplierId]) grouped[sup.supplierId] = [];
        grouped[sup.supplierId].push(sup);
      });

      const rowTotal = Object.values(grouped).reduce(
        (subSum: number, entries: any[]) => {
          let chosen: any[] = entries;
          if (activeLocs.length > 0) {
            // SPECIFIC LOCATIONS: Include ONLY entries for those locations
            const locEntries = entries.filter((e) =>
              activeLocs.includes((e.locationId || "").toString()),
            );
            chosen = locEntries;
          } else {
            // GLOBAL: uključi SVE unose (i globalne i lokacijske)
            chosen = entries;
          }

          return (
            subSum +
            chosen.reduce((s: number, sup: any) => {
              let supplierPaid = 0;

              // Default value - check payment status
              const defaultValue = Number(sup.defaultValue) || 0;
              const defaultStatus = sup.defaultPaymentStatus || "unpaid";

              if (defaultStatus === "paid") {
                supplierPaid += defaultValue;
              } else if (
                defaultStatus === "partial" &&
                sup.defaultPartialPayment &&
                defaultValue > 0 // Only count partialPayment if there's actual baseline
              ) {
                supplierPaid += sup.defaultPartialPayment.paidAmount || 0;
              }

              // Custom entries - check individual payment status
              if (sup.customEntries && Array.isArray(sup.customEntries)) {
                sup.customEntries.forEach((entry: any) => {
                  const entryStatus = entry.paymentStatus || "unpaid";
                  const entryAmount = Number(entry.amount) || 0;

                  if (entryStatus === "paid") {
                    supplierPaid += entryAmount;
                  } else if (
                    entryStatus === "partial" &&
                    entry.partialPayment
                  ) {
                    supplierPaid += entry.partialPayment.paidAmount || 0;
                  }
                });
              }

              // Daily revenue entries - defaultValue no longer includes them, count separately
              if (
                sup.dailyRevenueEntries &&
                Array.isArray(sup.dailyRevenueEntries)
              ) {
                sup.dailyRevenueEntries.forEach((entry: any) => {
                  const entryStatus = entry.paymentStatus || "paid"; // Default is PAID for daily revenue
                  const entryAmount = Number(entry.amount) || 0;

                  if (entryStatus === "paid") {
                    supplierPaid += entryAmount;
                  } else if (
                    entryStatus === "partial" &&
                    entry.partialPayment
                  ) {
                    supplierPaid += entry.partialPayment.paidAmount || 0;
                  }
                });
              }

              return s + supplierPaid;
            }, 0)
          );
        },
        0,
      );

      return sum + rowTotal;
    }, 0);
  }, [matrix, locationFilters]);

  const totalDeductions = deductionsFromRevenue + externalExpensesFromMatrix;
  const totalIncomeWithExtra = income + extraIncomeTotal;
  const netResult = totalIncomeWithExtra - totalDeductions;

  // Debug logging for all financial calculations
  //   console.log("═══════════════════════════════════════════════════════");
  //   console.log("[FINANCIAL SUMMARY]");
  //   console.log("───────────────────────────────────────────────────────");
  //   console.log(`Income: ${income.toLocaleString("sr-RS")} RSD`);
  //   console.log(`Extra Income: ${extraIncomeTotal.toLocaleString("sr-RS")} RSD`);
  //   console.log(
  //     `Total Income (with extra): ${totalIncomeWithExtra.toLocaleString(
  //       "sr-RS",
  //     )} RSD`,
  //   );
  //   console.log("───────────────────────────────────────────────────────");
  //   console.log(
  //     `Deductions from Daily Revenue: ${deductionsFromRevenue.toLocaleString(
  //       "sr-RS",
  //     )} RSD`,
  //   );
  //   console.log(
  //     `Operational Expenses (externalExpensesFromMatrix): ${externalExpensesFromMatrix.toLocaleString(
  //       "sr-RS",
  //     )} RSD`,
  //   );
  //   console.log(
  //     `Total Deductions: ${totalDeductions.toLocaleString("sr-RS")} RSD`,
  //   );
  //   console.log("───────────────────────────────────────────────────────");
  //   console.log(`Paid Expenses: ${paidExpenses.toLocaleString("sr-RS")} RSD`);
  //   console.log(`Unpaid Expenses: ${unpaidExpenses.toLocaleString("sr-RS")} RSD`);
  //   console.log(
  //     `Total (Paid + Unpaid): ${(paidExpenses + unpaidExpenses).toLocaleString(
  //       "sr-RS",
  //     )} RSD`,
  //   );

  // Verification: paid + unpaid should equal externalExpensesFromMatrix
  const paymentStatusTotal = paidExpenses + unpaidExpenses;
  const difference = Math.abs(paymentStatusTotal - externalExpensesFromMatrix);
  if (difference > 0.01) {
    console.warn(
      `⚠️  MISMATCH: Paid + Unpaid (${paymentStatusTotal.toLocaleString(
        "sr-RS",
      )}) ≠ External Expenses (${externalExpensesFromMatrix.toLocaleString(
        "sr-RS",
      )})`,
    );
    console.warn(`   Difference: ${difference.toLocaleString("sr-RS")} RSD`);
    console.warn(
      `   Paid + Unpaid is ${
        paymentStatusTotal > externalExpensesFromMatrix ? "HIGHER" : "LOWER"
      } than External Expenses`,
    );
    console.warn(`   This could indicate:`);
    console.warn(`   - Some expenses don't have payment status set`);
    console.warn(`   - Daily revenue entries have incorrect default status`);
    console.warn(
      `   - Data synchronization issue between totalValue and payment tracking`,
    );

    // Detailed breakdown by category
    if (matrix && Array.isArray(matrix.rows)) {
      console.group("📊 Detailed Category Breakdown:");
      matrix.rows.forEach((row: any) => {
        const categoryName = row.categoryName;
        let categoryTotal = 0;
        let categoryPaid = 0;
        let categoryUnpaid = 0;

        const grouped: Record<string, any[]> = {};
        // Prikazuj SVE suppliers, ali u kalkulacije idu samo oni SA locationId
        (row.suppliers || []).forEach((sup: any) => {
          if (!grouped[sup.supplierId]) grouped[sup.supplierId] = [];
          grouped[sup.supplierId].push(sup);
        });

        Object.values(grouped).forEach((entries: any[]) => {
          // Filtriraj: ra\u010dunaj samo suppliers SA locationId
          const withLocation = entries.filter((e) => e.locationId);

          withLocation.forEach((sup: any) => {
            // Use getDisplayedTotal instead of sup.totalValue to ensure consistency
            const totalValue = getDisplayedTotal(row.categoryId, sup);
            categoryTotal += totalValue;

            // Calculate paid portion
            const defaultValue = Number(sup.defaultValue) || 0;
            const defaultStatus = sup.defaultPaymentStatus || "unpaid";

            if (defaultStatus === "paid") {
              categoryPaid += defaultValue;
            } else if (defaultStatus === "unpaid") {
              categoryUnpaid += defaultValue;
            } else if (
              defaultStatus === "partial" &&
              sup.defaultPartialPayment &&
              defaultValue > 0 // Only count partialPayment if there's actual baseline
            ) {
              categoryPaid += sup.defaultPartialPayment.paidAmount || 0;
              categoryUnpaid += sup.defaultPartialPayment.remainingAmount || 0;
            }

            // Custom entries
            if (Array.isArray(sup.customEntries)) {
              sup.customEntries.forEach((entry: any) => {
                const amount = Number(entry.amount) || 0;
                const status = entry.paymentStatus || "unpaid";

                if (status === "paid") {
                  categoryPaid += amount;
                } else if (status === "unpaid") {
                  categoryUnpaid += amount;
                } else if (status === "partial" && entry.partialPayment) {
                  categoryPaid += entry.partialPayment.paidAmount || 0;
                  categoryUnpaid += entry.partialPayment.remainingAmount || 0;
                }
              });
            }

            // Daily revenue entries
            if (Array.isArray(sup.dailyRevenueEntries)) {
              sup.dailyRevenueEntries.forEach((entry: any) => {
                const amount = Number(entry.amount) || 0;
                const status = entry.paymentStatus || "paid"; // DEFAULT IS PAID!

                if (status === "paid") {
                  categoryPaid += amount;
                } else if (status === "unpaid") {
                  categoryUnpaid += amount;
                } else if (status === "partial" && entry.partialPayment) {
                  categoryPaid += entry.partialPayment.paidAmount || 0;
                  categoryUnpaid += entry.partialPayment.remainingAmount || 0;
                }
              });
            }
          });
        });

        const categoryPaymentTotal = categoryPaid + categoryUnpaid;
        const categoryDiff = categoryTotal - categoryPaymentTotal;

        if (Math.abs(categoryDiff) > 0.01) {
          //   console.log(`❌ ${categoryName}:`);
          //   console.log(`   Total: ${categoryTotal.toLocaleString("sr-RS")} RSD`);
          //   console.log(`   Paid: ${categoryPaid.toLocaleString("sr-RS")} RSD`);
          //   console.log(
          //     `   Unpaid: ${categoryUnpaid.toLocaleString("sr-RS")} RSD`,
          //   );
          //   console.log(
          //     `   Paid+Unpaid: ${categoryPaymentTotal.toLocaleString(
          //       "sr-RS",
          //     )} RSD`,
          //   );
          //   console.log(
          //     `   MISSING: ${categoryDiff.toLocaleString("sr-RS")} RSD`,
          //   );
        }
      });
      console.groupEnd();
    }
  } else {
    // console.log(
    //   `✓  Payment status verification OK (difference: ${difference.toFixed(
    //     2,
    //   )} RSD)`,
    // );
  }

  //   console.log("───────────────────────────────────────────────────────");
  //   console.log(`Net Result: ${netResult.toLocaleString("sr-RS")} RSD`);
  //   console.log(
  //     `Location Filter: ${
  //       locationFilters.length > 0
  //         ? locationFilters.join(", ")
  //         : "All locations (Global)"
  //     }`,
  //   );
  //   console.log("═══════════════════════════════════════════════════════");

  // ============================
  //   ALL SUPPLIERS FROM CATEGORIES
  // ============================
  // Get all suppliers from all categories with their category info
  const allSuppliers = useMemo(() => {
    if (!categories || categories.length === 0) return [];
    const suppliersMap = new Map<string, any>();

    categories.forEach((category: any) => {
      // Skip frozen categories - don't allow adding their suppliers
      if (category.isFrozen) return;

      category.suppliers?.forEach((sup: any) => {
        const supplierId = sup._id?.toString() || sup._id;

        // Only add if not already in map (use first occurrence)
        if (!suppliersMap.has(supplierId)) {
          suppliersMap.set(supplierId, {
            supplierId: supplierId,
            supplierName: sup.name,
            categoryId: category._id,
            categoryName: category.name,
          });
        }
      });
    });

    return Array.from(suppliersMap.values());
  }, [categories]);

  // ============================
  //   FILTERI MATRICE
  // ============================
  const filteredRows = useMemo(() => {
    if (!matrix) return [];

    // If no category/supplier selected, don't render matrix rows
    if (categoryFilter === "all" && supplierFilter === "all") {
      return [];
    }

    let result = matrix.rows;

    // Filter by category if selected
    if (categoryFilter !== "all") {
      result = result.filter((r: any) => r.categoryId === categoryFilter);
    }

    // Filter by supplier if selected (independent of category)
    if (supplierFilter !== "all") {
      // Find the selected supplier info from categories
      const selectedSupplier = allSuppliers.find(
        (s: any) => s.supplierId === supplierFilter,
      );

      if (selectedSupplier) {
        // Check if supplier exists in any matrix row
        const hasSupplierInMatrix = result.some((r: any) =>
          r.suppliers.some((s: any) => s.supplierId === supplierFilter),
        );

        if (hasSupplierInMatrix) {
          // Filter existing suppliers
          result = result
            .map((r: any) => ({
              ...r,
              suppliers: r.suppliers.filter(
                (s: any) => s.supplierId === supplierFilter,
              ),
            }))
            .filter((r: any) => r.suppliers.length > 0);
        } else {
          // Supplier doesn't exist in matrix - create empty entry in their category
          const categoryRow = result.find(
            (r: any) => r.categoryId === selectedSupplier.categoryId,
          );

          if (categoryRow) {
            result = [
              {
                ...categoryRow,
                suppliers: [
                  {
                    supplierId: selectedSupplier.supplierId,
                    supplierName: selectedSupplier.supplierName,
                    locationId: null,
                    defaultValue: 0,
                    customValue: 0,
                    totalValue: 0,
                    customEntries: [],
                    excludeFromBusiness: false,
                  },
                ],
              },
            ];
          } else {
            // Category doesn't exist in matrix either - create both
            result = [
              {
                categoryId: selectedSupplier.categoryId,
                categoryName: selectedSupplier.categoryName,
                suppliers: [
                  {
                    supplierId: selectedSupplier.supplierId,
                    supplierName: selectedSupplier.supplierName,
                    locationId: null,
                    defaultValue: 0,
                    customValue: 0,
                    totalValue: 0,
                    customEntries: [],
                    excludeFromBusiness: false,
                  },
                ],
              },
            ];
          }
        }
      }
    }

    return result;
  }, [matrix, categoryFilter, supplierFilter, allSuppliers]);

  const categoryOptions = useMemo(() => {
    if (!categories || categories.length === 0) return [];
    return categories.map((cat: any) => ({
      id: cat._id,
      name: cat.name,
      isFrozen: cat.isFrozen,
    }));
  }, [categories]);

  const currentCategorySuppliers = useMemo(() => {
    if (!matrix || categoryFilter === "all") return [];
    const row = matrix.rows.find((r: any) => r.categoryId === categoryFilter);

    if (!row || !row.suppliers) return [];

    // Deduplicate suppliers by supplierId
    const suppliersMap = new Map<string, any>();
    row.suppliers.forEach((sup: any) => {
      const supplierId = sup.supplierId?.toString() || sup.supplierId;
      if (!suppliersMap.has(supplierId)) {
        suppliersMap.set(supplierId, {
          supplierId: supplierId,
          supplierName: sup.supplierName,
          categoryId: row.categoryId,
          categoryName: row.categoryName,
        });
      }
    });

    return Array.from(suppliersMap.values());
  }, [matrix, categoryFilter]);

  // Get suppliers to display based on selected category
  const displayedSuppliers = useMemo(() => {
    // Always show all suppliers, regardless of category filter
    return allSuppliers;
  }, [allSuppliers]);

  const handlePartialPaymentConfirm = () => {
    if (
      !matrix ||
      !partialPaymentModal.categoryId ||
      !partialPaymentModal.supplierId
    )
      return;

    const { paidAmount, remainingAmount } = partialPaymentModal;

    // Get current totalValue from matrix (in case custom entries were added)
    const row = matrix.rows.find(
      (r: any) => r.categoryId === partialPaymentModal.categoryId,
    );
    if (!row) return;

    const supplier = row.suppliers.find(
      (s: any) =>
        s.supplierId === partialPaymentModal.supplierId &&
        (s.locationId?.toString() || null) ===
          (partialPaymentModal.locationId?.toString() || null),
    );
    if (!supplier) return;

    // Get current total value including daily revenue
    const dailyAdded = getDailyAdded(partialPaymentModal.categoryId, supplier);
    const currentTotalValue = (supplier.totalValue || 0) + dailyAdded;

    // Validation: paidAmount + remainingAmount should equal current totalValue
    const total = paidAmount + remainingAmount;
    if (Math.abs(total - currentTotalValue) > 0.01) {
      message.error(
        `${t("Paid amount")} + ${t(
          "Remaining amount",
        )} mora biti jednako ukupnom iznosu (${currentTotalValue.toLocaleString(
          "sr-RS",
        )} RSD). Trenutni zbir: ${total.toLocaleString("sr-RS")} RSD`,
      );
      return;
    }

    const newMatrix = { ...matrix };
    const newRow = newMatrix.rows.find(
      (r: any) => r.categoryId === partialPaymentModal.categoryId,
    );
    const newSupplier = newRow.suppliers.find(
      (s: any) =>
        s.supplierId === partialPaymentModal.supplierId &&
        (s.locationId?.toString() || null) ===
          (partialPaymentModal.locationId?.toString() || null),
    );

    newSupplier.paymentStatus = "partial";
    newSupplier.partialPayment = { paidAmount, remainingAmount };

    setMatrix(newMatrix);
    setPartialPaymentModal({
      visible: false,
      categoryId: null,
      supplierId: null,
      locationId: null,
      totalAmount: 0,
      paidAmount: 0,
      remainingAmount: 0,
    });
  };

  const handlePartialPaymentCancel = () => {
    setPartialPaymentModal({
      visible: false,
      categoryId: null,
      supplierId: null,
      locationId: null,
      totalAmount: 0,
      paidAmount: 0,
      remainingAmount: 0,
    });
  };

  // Custom Entry Partial Payment Handlers
  const handleCustomEntryPartialPaymentConfirm = async () => {
    if (
      !matrix ||
      !clientId ||
      !month ||
      !customEntryPartialModal.categoryId ||
      !customEntryPartialModal.supplierId ||
      customEntryPartialModal.entryIndex === null
    )
      return;

    const {
      paidAmount,
      remainingAmount,
      entryIndex,
      categoryId,
      supplierId,
      locationId,
    } = customEntryPartialModal;

    const row = matrix.rows.find((r: any) => r.categoryId === categoryId);
    if (!row) return;

    const supplier = row.suppliers.find(
      (s: any) =>
        s.supplierId === supplierId &&
        (s.locationId?.toString() || null) === (locationId?.toString() || null),
    );
    if (
      !supplier ||
      !supplier.customEntries ||
      !supplier.customEntries[entryIndex]
    )
      return;

    const currentEntry = supplier.customEntries[entryIndex];
    const currentAmount = Number(currentEntry.amount) || 0;

    // Validation: paidAmount + remainingAmount should equal current amount
    const total = paidAmount + remainingAmount;
    if (Math.abs(total - currentAmount) > 0.01) {
      message.error(
        `${t("Paid amount")} + ${t(
          "Remaining amount",
        )} mora biti jednako ukupnom iznosu (${currentAmount.toLocaleString(
          "sr-RS",
        )} RSD). Trenutni zbir: ${total.toLocaleString("sr-RS")} RSD`,
      );
      return;
    }

    // Determine payment status based on amounts
    let paymentStatus = "partial";
    if (remainingAmount === 0 || paidAmount >= currentAmount) {
      paymentStatus = "paid";
    } else if (paidAmount === 0 || remainingAmount >= currentAmount) {
      paymentStatus = "unpaid";
    }

    try {
      // Build custom entries with updated payment status
      const customEntries = supplier.customEntries.map(
        (ce: any, idx: number) => {
          if (idx === entryIndex) {
            return {
              description: ce.description || "",
              amount: Number(ce.amount) || 0,
              paymentStatus,
              partialPayment: { paidAmount, remainingAmount },
            };
          }
          return {
            description: ce.description || "",
            amount: Number(ce.amount) || 0,
            paymentStatus: ce.paymentStatus || "unpaid",
            partialPayment: ce.partialPayment || {
              paidAmount: 0,
              remainingAmount: 0,
            },
          };
        },
      );

      const customValue = customEntries.reduce(
        (sum: number, ce: any) => sum + (Number(ce.amount) || 0),
        0,
      );

      // Send to API
      await axios.put(`${BASE_API_URL}/business/expense-matrix/category`, {
        clientId,
        month,
        categoryId,
        suppliers: [
          {
            supplierId: supplier.supplierId,
            locationId: supplier.locationId,
            customEntries,
            customValue,
          },
        ],
      });

      message.success(t("Saved successfully"));

      // Refresh matrix data
      await fetchMatrix();

      setCustomEntryPartialModal({
        visible: false,
        categoryId: null,
        supplierId: null,
        locationId: null,
        entryIndex: null,
        totalAmount: 0,
        paidAmount: 0,
        remainingAmount: 0,
      });
    } catch (err) {
      console.error(err);
      message.error(t("Error while saving"));
    }
  };

  const handleCustomEntryPartialPaymentCancel = () => {
    setCustomEntryPartialModal({
      visible: false,
      categoryId: null,
      supplierId: null,
      locationId: null,
      entryIndex: null,
      totalAmount: 0,
      paidAmount: 0,
      remainingAmount: 0,
    });
  };

  // Daily Revenue Entry Payment Status Handlers
  const handleDailyRevenuePaymentClick = (
    entry: any,
    categoryId: string,
    supplierId: string,
    locationId?: string | null,
  ) => {
    const status = entry.paymentStatus || "paid";
    const amount = Number(entry.amount) || 0;
    const paidAmt =
      status === "partial"
        ? entry.partialPayment?.paidAmount || 0
        : status === "paid"
          ? amount
          : 0;
    const remainingAmt =
      status === "partial"
        ? entry.partialPayment?.remainingAmount || 0
        : status === "paid"
          ? 0
          : amount;

    console.log("[handleDailyRevenuePaymentClick] Entry data:", {
      entryId: entry.entryId,
      date: entry.date,
      amount: entry.amount,
      status: entry.paymentStatus,
    });

    setDailyRevenuePaymentModal({
      visible: true,
      entryId: entry.entryId || null,
      categoryId: categoryId,
      supplierId: supplierId,
      locationId: locationId || null,
      date: entry.date || null,
      totalAmount: amount,
      currentStatus: status,
      paidAmount: paidAmt,
      remainingAmount: remainingAmt,
    });
  };

  const handleDailyRevenuePaymentStatusChange = (newStatus: string) => {
    const { totalAmount } = dailyRevenuePaymentModal;

    if (newStatus === "paid") {
      setDailyRevenuePaymentModal((prev) => ({
        ...prev,
        currentStatus: "paid",
        paidAmount: totalAmount,
        remainingAmount: 0,
      }));
    } else if (newStatus === "unpaid") {
      setDailyRevenuePaymentModal((prev) => ({
        ...prev,
        currentStatus: "unpaid",
        paidAmount: 0,
        remainingAmount: totalAmount,
      }));
    } else if (newStatus === "partial") {
      // Keep current values for partial
      setDailyRevenuePaymentModal((prev) => ({
        ...prev,
        currentStatus: "partial",
      }));
    }
  };

  // Quick status change for custom entries from unpaid modal
  const handleQuickCustomEntryStatusChange = async (
    item: any,
    newStatus: string,
  ) => {
    if (!matrix || !clientId || !month) {
      message.error(t("Missing data to save"));
      return;
    }

    try {
      // Find the supplier in matrix
      const row = matrix.rows.find(
        (r: any) => r.categoryId === item.categoryId,
      );
      if (!row) return;

      const supplier = row.suppliers.find(
        (s: any) =>
          s.supplierId === item.supplierId &&
          (s.locationId?.toString() || null) ===
            (item.locationId?.toString() || null),
      );
      if (!supplier) return;

      // Build custom entries with updated status
      const customEntries = Array.isArray(supplier.customEntries)
        ? supplier.customEntries.map((ce: any, idx: number) => {
            if (idx === item.entryIndex) {
              return {
                description: ce.description || "",
                amount: Number(ce.amount) || 0,
                paymentStatus: newStatus,
                partialPayment: { paidAmount: 0, remainingAmount: 0 },
              };
            }
            return {
              description: ce.description || "",
              amount: Number(ce.amount) || 0,
              paymentStatus: ce.paymentStatus || "unpaid",
              partialPayment: ce.partialPayment || {
                paidAmount: 0,
                remainingAmount: 0,
              },
            };
          })
        : [];

      const customValue = customEntries.reduce(
        (sum: number, ce: any) => sum + (Number(ce.amount) || 0),
        0,
      );

      // Send to API
      await axios.put(`${BASE_API_URL}/business/expense-matrix/category`, {
        clientId,
        month,
        categoryId: item.categoryId,
        suppliers: [
          {
            supplierId: supplier.supplierId,
            locationId: supplier.locationId,
            customEntries,
            customValue,
          },
        ],
      });

      message.success(t("Payment status updated successfully"));

      // Refresh matrix data
      await fetchMatrix();
    } catch (error) {
      console.error("Error updating custom entry payment status:", error);
      message.error(t("Failed to update payment status"));
    }
  };

  // Quick status change for default value from unpaid modal
  const handleQuickDefaultValueStatusChange = async (
    item: any,
    newStatus: string,
  ) => {
    if (!matrix || !clientId || !month) {
      message.error(t("Missing data to save"));
      return;
    }

    try {
      // Find the supplier in matrix
      const row = matrix.rows.find(
        (r: any) => r.categoryId === item.categoryId,
      );
      if (!row) return;

      const supplier = row.suppliers.find(
        (s: any) =>
          s.supplierId === item.supplierId &&
          (s.locationId?.toString() || null) ===
            (item.locationId?.toString() || null),
      );
      if (!supplier) return;

      const defaultValue = Number(supplier.defaultValue) || 0;

      // Send to API with default payment status
      await axios.put(`${BASE_API_URL}/business/expense-matrix/category`, {
        clientId,
        month,
        categoryId: item.categoryId,
        suppliers: [
          {
            supplierId: supplier.supplierId,
            locationId: supplier.locationId,
            defaultPaymentStatus: newStatus,
            defaultPartialPayment: {
              paidAmount: newStatus === "paid" ? defaultValue : 0,
              remainingAmount: newStatus === "paid" ? 0 : defaultValue,
            },
          },
        ],
      });

      message.success(t("Payment status updated successfully"));

      // Refresh matrix data
      await fetchMatrix();
    } catch (error) {
      console.error("Error updating default value payment status:", error);
      message.error(t("Failed to update payment status"));
    }
  };

  // Quick status change for daily revenue from unpaid modal
  const handleQuickDailyRevenueStatusChange = async (
    item: any,
    newStatus: string,
  ) => {
    try {
      console.log("[handleQuickDailyRevenueStatusChange] Updating entry:", {
        entryId: item.entryId,
        categoryId: item.categoryId,
        date: item.date,
        amount: item.amount,
        newStatus,
      });

      await axios.put(
        `${BASE_API_URL}/admin/${userData?.clientId}/updateDailyRevenueEntryPaymentStatus`,
        {
          entryId: item.entryId,
          categoryId: item.categoryId,
          supplierId: item.supplierId,
          locationId: item.locationId,
          date: item.date,
          amount: item.amount,
          paymentStatus: newStatus,
          partialPayment: { paidAmount: 0, remainingAmount: 0 },
        },
      );

      message.success(t("Payment status updated successfully"));

      // Refresh data
      await fetchMatrix();
      if (dateRange && dateRange[0] && dateRange[1]) {
        await fetchDailyRevenue(dateRange[0], dateRange[1]);
      }
    } catch (error) {
      console.error("Error updating daily revenue payment status:", error);
      message.error(t("Failed to update payment status"));
    }
  };

  const handleDailyRevenuePaymentSave = async () => {
    if (!matrix) return;

    const {
      currentStatus,
      paidAmount,
      remainingAmount,
      totalAmount,
      categoryId,
      supplierId,
      locationId,
      date,
      entryId,
    } = dailyRevenuePaymentModal;

    // Check if we have required fields
    if (!categoryId || !supplierId || !date) {
      message.error(t("Missing required information"));
      return;
    }

    console.log("[handleDailyRevenuePaymentSave] Modal state:", {
      entryId,
      categoryId,
      supplierId,
      locationId,
      date,
      totalAmount,
      currentStatus,
    });

    // Validation for partial
    if (currentStatus === "partial") {
      const total = paidAmount + remainingAmount;
      if (Math.abs(total - totalAmount) > 0.01) {
        message.error(
          `${t("Paid amount")} + ${t(
            "Remaining amount",
          )} mora biti jednako ukupnom iznosu (${totalAmount.toLocaleString(
            "sr-RS",
          )} RSD). Trenutni zbir: ${total.toLocaleString("sr-RS")} RSD`,
        );
        return;
      }
      if (paidAmount <= 0 || remainingAmount <= 0) {
        message.error(t("expensesMatrix.partialModal.invalidAmount"));
        return;
      }
    }

    try {
      // Update via API
      console.log("[handleDailyRevenuePaymentSave] Sending request with:", {
        categoryId,
        supplierId,
        date,
        paymentStatus: currentStatus,
        partialPayment:
          currentStatus === "partial"
            ? { paidAmount, remainingAmount }
            : { paidAmount: 0, remainingAmount: 0 },
      });

      await axios.put(
        `${BASE_API_URL}/admin/${userData?.clientId}/updateDailyRevenueEntryPaymentStatus`,
        {
          entryId: entryId,
          categoryId: categoryId,
          supplierId: supplierId,
          locationId: locationId,
          date: date,
          amount: totalAmount,
          paymentStatus: currentStatus,
          partialPayment:
            currentStatus === "partial"
              ? { paidAmount, remainingAmount }
              : { paidAmount: 0, remainingAmount: 0 },
        },
      );

      message.success(t("Payment status updated successfully"));

      // Refresh data - need to refresh both matrix and daily revenue
      await fetchMatrix();
      if (dateRange && dateRange[0] && dateRange[1]) {
        await fetchDailyRevenue(dateRange[0], dateRange[1]);
      }

      setDailyRevenuePaymentModal({
        visible: false,
        entryId: null,
        categoryId: null,
        supplierId: null,
        locationId: null,
        date: null,
        totalAmount: 0,
        currentStatus: "paid",
        paidAmount: 0,
        remainingAmount: 0,
      });
    } catch (error) {
      console.error("Error updating daily revenue payment status:", error);
      message.error(t("Failed to update payment status"));
    }
  };

  const handleDailyRevenuePaymentCancel = () => {
    setDailyRevenuePaymentModal({
      visible: false,
      entryId: null,
      categoryId: null,
      supplierId: null,
      locationId: null,
      date: null,
      totalAmount: 0,
      currentStatus: "paid",
      paidAmount: 0,
      remainingAmount: 0,
    });
  };

  const renderDailyDetail = (
    rowId: string,
    supId: string,
    locationId?: string | null,
  ) => {
    const key = createDailyExpenseKey(rowId, supId, locationId);
    const detail = dailyExpenseDetail[key];
    if (!detail) return null;
    const entries = Array.isArray(detail.entries) ? detail.entries : [];
    const sorted = entries.sort(
      (a, b) => dayjs(b.date).valueOf() - dayjs(a.date).valueOf(),
    );
    return (
      <div style={{ minWidth: 220 }}>
        <div
          style={{
            marginBottom: 8,
            fontWeight: 600,
            borderBottom: "1px solid #f0f0f0",
            paddingBottom: 4,
          }}
        >
          {t("Daily Revenue Entries")}
        </div>
        {sorted.map((entry, idx) => (
          <div
            key={`${entry.date}-${idx}`}
            onClick={() =>
              handleDailyRevenuePaymentClick(entry, rowId, supId, locationId)
            }
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              marginBottom: 4,
              alignItems: "center",
              cursor: "pointer",
              padding: "4px 8px",
              borderRadius: "4px",
              transition: "background-color 0.2s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#f5f5f5";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
            }}
          >
            <span style={{ color: "#595959", flex: 1 }}>
              {dayjs(entry.date).format("DD.MM.YYYY")}
              {entry.location && (
                <span style={{ color: "#8c8c8c" }}> · {entry.location}</span>
              )}
            </span>
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <span style={{ fontWeight: 500 }}>
                {Number(entry.amount || 0).toLocaleString("sr-RS")} RSD
              </span>
              {/* Payment status indicator */}
              {(() => {
                const status = entry.paymentStatus || "paid";
                const color =
                  status === "paid"
                    ? "#52c41a"
                    : status === "partial"
                      ? "#faad14"
                      : "#ff4d4f";
                const tooltipText =
                  status === "paid"
                    ? t("Plaćeno")
                    : status === "partial"
                      ? `${t("Delimično plaćeno")}: ${
                          entry.partialPayment?.paidAmount || 0
                        }/${entry.amount} RSD`
                      : t("Neplaćeno");
                return (
                  <Tooltip title={tooltipText}>
                    <div
                      style={{
                        width: 12,
                        height: 12,
                        borderRadius: "50%",
                        background: color,
                        border: `1px solid ${color}`,
                        flexShrink: 0,
                      }}
                    />
                  </Tooltip>
                );
              })()}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // ============================
  //   RENDER
  // ============================
  if (!canViewSummary && !canViewExpenses) {
    return <div className="mainContainer">{t("No permission to view")}</div>;
  }

  // Show loading overlay while any data is being fetched
  if (isPageLoading) {
    return (
      <div
        className="expenses-page"
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          background: "#f0f2f5",
        }}
      >
        <Spin size="large" tip={t("Loading...")} />
      </div>
    );
  }

  return (
    <div className="expenses-page">
      {/* PAGE HEADER WITH CONTROLS */}
      <div className="expenses-page-header">
        <div style={{ flex: 1 }}>
          <Title level={2} className="expenses-page-title">
            {t("Expense matrix")}
          </Title>
          <Text type="secondary" className="expenses-page-subtitle">
            {t("Expense matrix subtitle")}
          </Text>
        </div>

        {/* PERIOD AND LOCATION CONTROLS */}
        <div
          className="header-controls"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <div
            className="nav-range-group"
            style={{ display: "flex", alignItems: "center", gap: 8 }}
          >
            <Button
              icon={<LeftOutlined />}
              onClick={handlePrevMonth}
              type="text"
              className="compact-nav-btn"
              style={{
                background: "rgba(255, 255, 255, 0.2)",
                border: "1px solid rgba(255, 255, 255, 0.3)",
                color: "#ffffff",
              }}
            />

            <div className="compact-range">
              <RangePicker
                value={dateRange}
                allowClear={false}
                format="DD.MM.YYYY"
                onCalendarChange={(v) =>
                  setDateRange([v?.[0] || null, v?.[1] || null])
                }
                style={{ minWidth: 260 }}
              />
            </div>

            <Button
              icon={<RightOutlined />}
              onClick={handleNextMonth}
              type="text"
              className="compact-nav-btn"
              style={{
                background: "rgba(255, 255, 255, 0.2)",
                border: "1px solid rgba(255, 255, 255, 0.3)",
                color: "#ffffff",
              }}
            />
          </div>

          <Select
            mode="multiple"
            className="location-filter"
            value={locationFilters.length > 0 ? locationFilters : undefined}
            allowClear={isSuperAdmin(userData?.roles)} // Only super admins can clear all
            showSearch
            placeholder={t("All locations")}
            optionFilterProp="children"
            maxTagCount="responsive"
            filterOption={(input, option: any) =>
              (option?.children as string)
                ?.toLowerCase()
                .includes(input.toLowerCase())
            }
            onChange={(vals: string[]) => {
              // Prevent deselecting all locations if user has assigned work locations
              if (
                !isSuperAdmin(userData?.roles) &&
                userWorkLocations.length > 0
              ) {
                if (!vals || vals.length === 0) {
                  message.warning(t("At least one location must be selected"));
                  return;
                }

                // Ensure user can only select from their assigned work locations
                const workLocationIds = userWorkLocations.map((wl: any) =>
                  wl._id.toString(),
                );
                const validSelections = vals.filter((v) =>
                  workLocationIds.includes(v),
                );

                if (validSelections.length === 0) {
                  message.warning(
                    t("You can only select your assigned locations"),
                  );
                  return;
                }

                setLocationFilters(validSelections);
              } else {
                // Super admins can select/deselect freely
                setLocationFilters(vals || []);
              }
            }}
            disabled={!canFilterByLocation}
          >
            {availableLocations.map((loc: any) => (
              <Option key={loc._id} value={loc._id?.toString()}>
                {loc.name}
              </Option>
            ))}
          </Select>
        </div>
      </div>

      {/* TOP CARDS – Modern Statistics */}
      {(isSuperAdmin(userData?.roles) ||
        hasPermission("business:view-financial-summary")) && (
        <div style={{ padding: "0 24px", marginTop: 24 }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={8}>
              <Card
                bordered={false}
                style={{
                  background:
                    "linear-gradient(135deg, #52c41a 0%, #389e0d 100%)",
                  borderRadius: 12,
                  overflow: "hidden",
                  boxShadow: "0 4px 12px rgba(82, 196, 26, 0.3)",
                  position: "relative",
                }}
              >
                <Tooltip title="Upravljaj dodatnim prihodima">
                  <Button
                    type="text"
                    icon={<PlusOutlined />}
                    onClick={() => setIsExtraIncomeModalOpen(true)}
                    className="addIncomeSalaryPlusButton"
                    style={{
                      position: "absolute",
                      top: 12,
                      right: 12,
                      color: "#fff",
                      fontSize: 18,
                    }}
                  />
                </Tooltip>
                <Statistic
                  title={
                    <span
                      style={{
                        color: "rgba(255,255,255,0.9)",
                        fontSize: 14,
                        fontWeight: 500,
                      }}
                    >
                      {t("Income")}
                    </span>
                  }
                  value={formatProtectedValue(totalIncomeWithExtra)}
                  prefix={<ArrowUpOutlined style={{ color: "#fff" }} />}
                  suffix={
                    <span
                      style={{ color: "rgba(255,255,255,0.9)", fontSize: 16 }}
                    >
                      RSD
                    </span>
                  }
                  valueStyle={{ color: "#fff", fontSize: 28, fontWeight: 700 }}
                />
              </Card>
            </Col>

            <Col xs={24} md={8}>
              <Card
                bordered={false}
                onClick={() => setDeductionsModalVisible(true)}
                style={{
                  background:
                    "linear-gradient(135deg, #ff4d4f 0%, #cf1322 100%)",
                  borderRadius: 12,
                  overflow: "hidden",
                  boxShadow: "0 4px 12px rgba(255, 77, 79, 0.3)",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                }}
                hoverable
              >
                <Statistic
                  title={
                    <span
                      style={{
                        color: "rgba(255,255,255,0.9)",
                        fontSize: 14,
                        fontWeight: 500,
                      }}
                    >
                      {t("Total deductions")}
                    </span>
                  }
                  value={formatProtectedValue(totalDeductions)}
                  prefix={<ArrowDownOutlined style={{ color: "#fff" }} />}
                  suffix={
                    <span
                      style={{ color: "rgba(255,255,255,0.9)", fontSize: 16 }}
                    >
                      RSD
                    </span>
                  }
                  valueStyle={{ color: "#fff", fontSize: 28, fontWeight: 700 }}
                />
              </Card>
            </Col>

            <Col xs={24} md={8}>
              <Card
                bordered={false}
                style={{
                  background:
                    netResult >= 0
                      ? "linear-gradient(135deg, #1890ff 0%, #096dd9 100%)"
                      : "linear-gradient(135deg, #faad14 0%, #d48806 100%)",
                  borderRadius: 12,
                  overflow: "hidden",
                  boxShadow:
                    netResult >= 0
                      ? "0 4px 12px rgba(24, 144, 255, 0.3)"
                      : "0 4px 12px rgba(250, 173, 20, 0.3)",
                }}
              >
                <Statistic
                  title={
                    <span
                      style={{
                        color: "rgba(255,255,255,0.9)",
                        fontSize: 14,
                        fontWeight: 500,
                      }}
                    >
                      {t("Net result")}
                    </span>
                  }
                  value={formatProtectedValue(netResult)}
                  suffix={
                    <span
                      style={{ color: "rgba(255,255,255,0.9)", fontSize: 16 }}
                    >
                      RSD
                    </span>
                  }
                  valueStyle={{ color: "#fff", fontSize: 28, fontWeight: 700 }}
                />
              </Card>
            </Col>
          </Row>
        </div>
      )}

      {/* DETALJNI PREGLED PRIHODA I ODBITAKA */}
      {(isSuperAdmin(userData?.roles) ||
        hasPermission("business:view-financial-review")) && (
        <Card className="exp-panel" bordered={false} style={{ marginTop: 24 }}>
          <div className="expenses-finance-header">
            <div className="expenses-finance-title">
              <Title level={4} style={{ margin: 0 }}>
                {t("Financial overview")}
              </Title>
              <Tag
                icon={<InfoCircleOutlined />}
                color="blue"
                className="expenses-finance-tag"
              >
                {t("How we got net amount")}
              </Tag>
            </div>
            <Text type="secondary" className="expenses-finance-period">
              {t("Period")}: {dateRange[0]?.format("DD.MM.YYYY")} –{" "}
              {dateRange[1]?.format("DD.MM.YYYY")}
            </Text>
          </div>

          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <div style={{ marginBottom: 8 }}>
                <Text type="secondary">{t("Income")}</Text>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginTop: 4,
                  }}
                >
                  <Text strong>{t("Total")}</Text>
                  <Text strong>
                    {summary
                      ? formatProtectedValue(summary.totalSales)
                      : income.toLocaleString("sr-RS")}{" "}
                    RSD
                  </Text>
                </div>
              </div>

              {extraIncomeTotal > 0 && (
                <div style={{ marginBottom: 8 }}>
                  <Text type="secondary">Dodatni prihodi</Text>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginTop: 4,
                    }}
                  >
                    <Text strong>Ukupno dodatnih prihoda</Text>
                    <Text strong style={{ color: "#52c41a" }}>
                      +{extraIncomeTotal.toLocaleString("sr-RS")} RSD
                    </Text>
                  </div>
                  {extraIncomeBreakdown.length > 0 && (
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 8,
                        marginTop: 8,
                      }}
                    >
                      {extraIncomeBreakdown.map((item, idx) => (
                        <Tag
                          key={idx}
                          color={item.color}
                          style={{ marginBottom: 4 }}
                        >
                          {item.typeName}: {item.total.toLocaleString("sr-RS")}{" "}
                          RSD ({item.count}x)
                        </Tag>
                      ))}
                    </div>
                  )}
                  <div style={{ marginTop: 16, textAlign: "center" }}>
                    <Button
                      type="primary"
                      ghost
                      onClick={() => setIsExtraIncomeModalOpen(true)}
                      style={{
                        borderRadius: 6,
                        fontWeight: 500,
                      }}
                      icon={<InfoCircleOutlined />}
                    >
                      Prikaži sve dodatne prihode
                    </Button>
                  </div>
                </div>
              )}

              <Divider style={{ margin: "12px 0" }} />
              <div style={{ marginBottom: 8 }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginTop: 4,
                  }}
                >
                  <Text strong style={{ fontSize: 16 }}>
                    Ukupan prihod (sa dodatnim)
                  </Text>
                  <Text strong style={{ fontSize: 16, color: "#52c41a" }}>
                    {totalIncomeWithExtra.toLocaleString("sr-RS")} RSD
                  </Text>
                </div>
              </div>

              <div style={{ marginTop: 8 }}>
                <Text type="secondary">{t("Income breakdown")}</Text>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 8,
                    marginTop: 8,
                  }}
                >
                  <Tag>
                    {t("Cash")}:{" "}
                    {summary ? formatProtectedValue(summary.breakdown.cash) : 0}{" "}
                    RSD
                  </Tag>
                  <Tag>
                    {t("Cards")}:{" "}
                    {summary ? formatProtectedValue(summary.breakdown.card) : 0}{" "}
                    RSD
                  </Tag>
                  <Tag>
                    {t("Delivery")}:{" "}
                    {summary
                      ? formatProtectedValue(summary.breakdown.delivery)
                      : 0}{" "}
                    RSD
                  </Tag>
                  <Tag>
                    {t("Bank transfer")}:{" "}
                    {summary
                      ? formatProtectedValue(summary.breakdown.bankTransfer)
                      : 0}{" "}
                    RSD
                  </Tag>
                </div>
              </div>
            </Col>

            <Col xs={24} md={12}>
              <Space
                direction="vertical"
                style={{ width: "100%" }}
                size="small"
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginTop: 4,
                  }}
                >
                  <Tooltip
                    title={
                      dailyRevenueDeductions.length > 0 ? (
                        <div style={{ minWidth: 240 }}>
                          {dailyRevenueDeductions
                            .sort(
                              (a, b) =>
                                dayjs(b.date, "DD.MM.YYYY").valueOf() -
                                dayjs(a.date, "DD.MM.YYYY").valueOf(),
                            )
                            .map((d, idx) => (
                              <div
                                key={`${d.date}-${idx}-${d.description}`}
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  gap: 8,
                                }}
                              >
                                <span>
                                  {d.date} · {d.description}
                                  {d.location ? ` (${d.location})` : ""}
                                </span>
                                <span>
                                  {d.amount.toLocaleString("sr-RS")} RSD
                                </span>
                              </div>
                            ))}
                        </div>
                      ) : (
                        t("No deductions")
                      )
                    }
                  >
                    <Text type="secondary" style={{ cursor: "pointer" }}>
                      {t("Deductions from daily revenue")}
                    </Text>
                  </Tooltip>
                  <Text>
                    {deductionsFromRevenue.toLocaleString("sr-RS")} RSD
                  </Text>
                </div>

                <div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginTop: 4,
                    }}
                  >
                    <Text type="secondary">{t("Operational expenses")}</Text>
                    <Text>
                      {externalExpensesFromMatrix.toLocaleString("sr-RS")} RSD
                    </Text>
                  </div>
                </div>

                <Divider style={{ margin: "8px 0" }} />

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginTop: 4,
                  }}
                >
                  <Text strong>{t("Net result")}</Text>
                  <Text
                    strong
                    style={{ color: netResult >= 0 ? "#52c41a" : "#cf1322" }}
                  >
                    {formatProtectedValue(netResult)} RSD
                  </Text>
                </div>
              </Space>
            </Col>
          </Row>
        </Card>
      )}

      {/* ZARADE ZAPOSLENIH */}
      {canViewExpenses && (
        <Card
          className="exp-panel expenses-salaries-panel"
          bordered={false}
          style={{ marginTop: 24 }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 8,
            }}
            className="expenses-salaries-header"
          >
            <Title level={4} style={{ margin: 0 }}>
              Zarade zaposlenih
            </Title>
            <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
              <Segmented
                size="small"
                value={salaryViewMode}
                onChange={(val) => setSalaryViewMode(val as any)}
                options={[
                  { label: "Lokacije", value: "location" },
                  { label: "Sektori", value: "sector" },
                ]}
              />
              <Text type="secondary">{selectedMonth.format("MMMM YYYY")}</Text>
            </div>
          </div>
          <Spin spinning={employeeEarningsLoading}>
            <Row gutter={[16, 16]}>
              {salaryViewMode === "location" &&
                Object.entries(employeeEarnings.byLocation || {}).map(
                  ([locId, loc]: any) => {
                    const selected =
                      locationFilters.length === 0 ||
                      locationFilters.includes(locId);
                    if (!selected) return null;
                    return (
                      <Col xs={24} md={12} lg={8} key={locId}>
                        <Card
                          size="small"
                          title={
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                              }}
                              className="location-card-title"
                            >
                              <span>{loc.locationName}</span>
                              <Tag color="blue">
                                {formatCurrency(loc.total)}
                              </Tag>
                            </div>
                          }
                          bordered
                        >
                          <List
                            size="small"
                            dataSource={(loc.employees || [])
                              .sort((a: any, b: any) => b.total - a.total)
                              .slice(
                                0,
                                expandedLocations[loc.locationName]
                                  ? undefined
                                  : 5,
                              )}
                            renderItem={(emp: any) => (
                              <List.Item style={{ padding: "6px 0" }}>
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    width: "100%",
                                  }}
                                >
                                  <span style={{ color: emp.isFrozenInPeriod ? '#999' : 'inherit' }}>
                                    {emp.name}
                                    {emp.isFrozenInPeriod && (
                                      <Tag color="default" style={{ marginLeft: 8, fontSize: '11px' }}>
                                        Zamrznut
                                      </Tag>
                                    )}
                                  </span>
                                  <span style={{ color: emp.isFrozenInPeriod ? '#999' : 'inherit' }}>
                                    {formatCurrency(emp.total)}
                                  </span>
                                </div>
                              </List.Item>
                            )}
                          />
                          {loc.employees?.length > 5 && (
                            <Button
                              type="default"
                              size="small"
                              onClick={() =>
                                setExpandedLocations((prev) => ({
                                  ...prev,
                                  [loc.locationName]: !prev[loc.locationName],
                                }))
                              }
                              style={{
                                marginTop: 8,
                                padding: "6px 10px",
                                borderRadius: 14,
                                border: "1px solid #e6f4ff",
                                background: expandedLocations[loc.locationName]
                                  ? "#f0f5ff"
                                  : "#f5faff",
                                color: "#1f3b73",
                                fontWeight: 600,
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                              }}
                            >
                              {expandedLocations[loc.locationName]
                                ? "Prikaži manje"
                                : `Prikaži sve (${loc.employees.length})`}
                              <span
                                style={{
                                  display: "inline-block",
                                  width: 16,
                                  height: 16,
                                  borderRadius: "50%",
                                  background: expandedLocations[
                                    loc.locationName
                                  ]
                                    ? "#f0f5ff"
                                    : "#e6f4ff",
                                  color: expandedLocations[loc.locationName]
                                    ? "#2f54eb"
                                    : "#1890ff",
                                  textAlign: "center",
                                  lineHeight: "16px",
                                  fontSize: 10,
                                  border: "1px solid rgba(24,144,255,0.25)",
                                }}
                              >
                                {expandedLocations[loc.locationName]
                                  ? "–"
                                  : "+"}
                              </span>
                            </Button>
                          )}
                        </Card>
                      </Col>
                    );
                  },
                )}
              {salaryViewMode === "sector" &&
                Object.entries(employeeEarnings.bySector || {}).map(
                  ([sectorId, sector]: any) => (
                    <Col xs={24} md={12} lg={8} key={sectorId}>
                      <Card
                        size="small"
                        title={
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            <span>{sector.sectorName}</span>
                            <Tag color="purple">
                              {formatCurrency(sector.total)}
                            </Tag>
                          </div>
                        }
                        bordered
                      >
                        <List
                          size="small"
                          dataSource={(sector.employees || [])
                            .sort((a: any, b: any) => b.total - a.total)
                            .slice(
                              0,
                              expandedLocations[sector.sectorName]
                                ? undefined
                                : 5,
                            )}
                          renderItem={(emp: any) => (
                            <List.Item style={{ padding: "6px 0" }}>
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  width: "100%",
                                }}
                              >
                                <span style={{ color: emp.isFrozenInPeriod ? '#999' : 'inherit' }}>
                                  {emp.name}
                                  {emp.isFrozenInPeriod && (
                                    <Tag color="default" style={{ marginLeft: 8, fontSize: '11px' }}>
                                      Zamrznut
                                    </Tag>
                                  )}
                                  {emp.locationName
                                    ? ` · ${emp.locationName}`
                                    : ""}
                                </span>
                                <span style={{ color: emp.isFrozenInPeriod ? '#999' : 'inherit' }}>
                                  {formatCurrency(emp.total)}
                                </span>
                              </div>
                            </List.Item>
                          )}
                        />
                        {sector.employees?.length > 5 && (
                          <Button
                            type="default"
                            size="small"
                            onClick={() =>
                              setExpandedLocations((prev) => ({
                                ...prev,
                                [sector.sectorName]: !prev[sector.sectorName],
                              }))
                            }
                            style={{
                              marginTop: 8,
                              padding: "6px 10px",
                              borderRadius: 14,
                              border: "1px solid #f0e8ff",
                              background: expandedLocations[sector.sectorName]
                                ? "#f6f0ff"
                                : "#faf5ff",
                              color: "#3f1d7a",
                              fontWeight: 600,
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                            }}
                          >
                            {expandedLocations[sector.sectorName]
                              ? "Prikaži manje"
                              : `Prikaži sve (${sector.employees.length})`}
                            <span
                              style={{
                                display: "inline-block",
                                width: 16,
                                height: 16,
                                borderRadius: "50%",
                                background: expandedLocations[sector.sectorName]
                                  ? "#f0e8ff"
                                  : "#f5edff",
                                color: expandedLocations[sector.sectorName]
                                  ? "#3f1d7a"
                                  : "#5a26b5",
                                textAlign: "center",
                                lineHeight: "16px",
                                fontSize: 10,
                                border: "1px solid rgba(90,38,181,0.25)",
                              }}
                            >
                              {expandedLocations[sector.sectorName] ? "–" : "+"}
                            </span>
                          </Button>
                        )}
                      </Card>
                    </Col>
                  ),
                )}
              {Object.keys(employeeEarnings.byLocation || {}).length === 0 && (
                <Col span={24}>
                  <Text type="secondary">
                    Nema obračuna zarade za ovaj mesec.
                  </Text>
                </Col>
              )}
            </Row>
          </Spin>
        </Card>
      )}

      {/* Payment Status Summary */}
      {(paidExpenses > 0 || unpaidExpenses > 0) && (
        <div style={{ padding: "0 24px", marginTop: 24 }}>
          <Row gutter={[16, 16]}>
            {/* Paid Expenses */}
            <Col xs={24} md={12}>
              <div
                style={{
                  padding: "16px 20px",
                  background: "rgba(82, 196, 26, 0.08)",
                  border: "1px solid rgba(82, 196, 26, 0.3)",
                  borderRadius: 8,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background: "#52c41a",
                    }}
                  />
                  <Text style={{ color: "#595959", fontSize: 14 }}>
                    {t("Paid expenses (from expense matrix)")}:
                  </Text>
                </div>
                <Text
                  strong
                  style={{
                    color: "#52c41a",
                    fontSize: 16,
                  }}
                >
                  {formatProtectedValue(paidExpenses)} RSD
                </Text>
              </div>
            </Col>

            {/* Unpaid Expenses */}
            <Col xs={24} md={12}>
              <div
                onClick={() => setUnpaidExpensesModalVisible(true)}
                style={{
                  padding: "16px 20px",
                  background: "rgba(255, 193, 7, 0.08)",
                  border: "1px solid rgba(255, 193, 7, 0.3)",
                  borderRadius: 8,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(255, 193, 7, 0.12)";
                  e.currentTarget.style.borderColor = "rgba(255, 193, 7, 0.5)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "rgba(255, 193, 7, 0.08)";
                  e.currentTarget.style.borderColor = "rgba(255, 193, 7, 0.3)";
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background: "#faad14",
                    }}
                  />
                  <Text style={{ color: "#595959", fontSize: 14 }}>
                    {t("Unpaid and partially unpaid expenses")}:
                  </Text>
                </div>
                <Text
                  strong
                  style={{
                    color: "#faad14",
                    fontSize: 16,
                  }}
                >
                  {formatProtectedValue(unpaidExpenses)} RSD
                </Text>
              </div>
            </Col>
          </Row>
        </div>
      )}

      {/* MATRIX – columns per category */}
      {!loading && (
        <Card className="exp-panel" bordered={false} style={{ marginTop: 24 }}>
          <div className="matrix-header">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 16,
                flexWrap: "wrap",
                flex: 1,
              }}
            >
              <Title level={4} style={{ margin: 0 }}>
                {t("Expense matrix")}
              </Title>

              {/* CATEGORY AND SUPPLIER FILTERS */}
              <Select
                showSearch
                allowClear
                value={categoryFilter}
                style={{ width: 220 }}
                onChange={(value) => {
                  setCategoryFilter(value || "all");
                }}
                disabled={!canFilterByCategory}
                filterOption={(input, option: any) => {
                  const categoryName =
                    categoryOptions.find((c: any) => c.id === option?.value)
                      ?.name || option?.value;
                  return (
                    option?.value === "all" ||
                    categoryName?.toLowerCase().includes(input.toLowerCase())
                  );
                }}
              >
                <Option value="all">{t("All categories")}</Option>
                {categoryOptions.map((c: any) => (
                  <Option key={c.id} value={c.id} disabled={c.isFrozen}>
                    {c.name}
                    {c.isFrozen ? ` (${t("Frozen")})` : ""}
                  </Option>
                ))}
              </Select>

              <Select
                showSearch
                allowClear
                style={{ width: 220 }}
                value={supplierFilter}
                onChange={(value) => {
                  setSupplierFilter(value || "all");
                }}
                placeholder={t("Supplier")}
                disabled={!canFilterByCategory}
                filterOption={(input, option: any) => {
                  const supplierName =
                    displayedSuppliers.find(
                      (s: any) => s.supplierId === option?.value,
                    )?.supplierName || option?.value;
                  return (
                    option?.value === "all" ||
                    supplierName?.toLowerCase().includes(input.toLowerCase())
                  );
                }}
              >
                <Option value="all">{t("All suppliers")}</Option>
                {displayedSuppliers.map((s: any) => (
                  <Option key={s.supplierId} value={s.supplierId}>
                    {s.supplierName}
                    {categoryFilter === "all" && s.categoryName && (
                      <span
                        style={{
                          color: "#999",
                          fontSize: "12px",
                          marginLeft: "8px",
                        }}
                      >
                        ({s.categoryName})
                      </span>
                    )}
                  </Option>
                ))}
              </Select>
            </div>

            <div style={{ display: "flex", gap: 8 }}>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                loading={saveAllLoading}
                onClick={saveAllChanges}
                disabled={!canUpdateMatrix}
              >
                {t("Save All")}
              </Button>
              <Button
                icon={<LeftOutlined />}
                type="text"
                onClick={scrollLeft}
              />
              <Button
                icon={<RightOutlined />}
                type="text"
                onClick={scrollRight}
              />
            </div>
          </div>

          {filteredRows.length === 0 ? (
            <div style={{ padding: 16 }}>
              <Text type="secondary">
                {t("Select a category or supplier to display the matrix.")}
              </Text>
            </div>
          ) : (
            <div ref={scrollContainerRef} className="matrix-grid-wrapper">
              <div className="matrix-grid">
                {filteredRows.map((row: any) => {
                  const suppliers = row.suppliers || [];

                  const groupedBySupplier: Record<string, any[]> = {};
                  // Prikazuj SVE suppliers (i sa i bez locationId)
                  // Ali samo oni SA locationId će biti uključeni u totalValue kalkulacije
                  suppliers.forEach((sup: any) => {
                    const key = sup.supplierId;
                    if (!groupedBySupplier[key]) groupedBySupplier[key] = [];
                    groupedBySupplier[key].push(sup);
                  });

                  const activeLocs =
                    locationFilters.length > 0 ? locationFilters : [];

                  // Za totalove računamo SAMO suppliers sa locationId
                  const suppliersForTotal = Object.values(
                    groupedBySupplier,
                  ).flatMap((entries: any[]) => {
                    // Filter: uključi samo suppliers SA locationId
                    const withLocation = entries.filter((e) => e.locationId);

                    if (activeLocs.length > 0) {
                      const locEntries = withLocation.filter((e) =>
                        activeLocs.includes((e.locationId || "").toString()),
                      );
                      if (locEntries.length) return locEntries;
                      return [];
                    }
                    // Global view: include ALL entries WITH locationId
                    return withLocation;
                  });

                  const categoryTotal = suppliersForTotal.reduce(
                    (s: number, sup: any) =>
                      s + getDisplayedTotal(row.categoryId, sup),
                    0,
                  );

                  return (
                    <div key={row.categoryId} className="matrix-card">
                      <div className="matrix-card-header">
                        <Text strong style={{ color: "#fff", fontSize: 14 }}>
                          {row.categoryName}
                        </Text>
                        <div>
                          <Text
                            style={{
                              fontSize: 12,
                              color: "rgba(255,255,255,0.85)",
                            }}
                          >
                            {t("Total")}:{" "}
                            <strong>
                              {categoryTotal.toLocaleString("sr-RS")} RSD
                            </strong>
                          </Text>
                        </div>
                      </div>
                      <div style={{ padding: "8px 10px" }}>
                        {Object.keys(groupedBySupplier).length === 0 && (
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {t("No suppliers")}
                          </Text>
                        )}

                        {Object.entries(groupedBySupplier).map(
                          ([supId, rawEntries]: [string, any[]]) => {
                            const hasLocation = rawEntries.some(
                              (e) => e.locationId,
                            );
                            let entries = rawEntries;

                            // Filter entries by selected locations
                            if (locationFilters.length > 0) {
                              const locEntries = rawEntries.filter((e) =>
                                locationFilters.includes(
                                  (e.locationId || "").toString(),
                                ),
                              );
                              if (locEntries.length) {
                                entries = locEntries;
                              } else {
                                const global = rawEntries.filter(
                                  (e) => !e.locationId,
                                );
                                if (global.length) entries = global;
                              }
                            }

                            const groupKey = `${row.categoryId}-${supId}`;
                            // When location filter is active, use first selected location for new entries
                            const activeFilteredLocation =
                              locationFilters.length > 0
                                ? locationFilters[0]
                                : null;
                            const selectedLoc =
                              supplierEditLocation[groupKey] ??
                              activeFilteredLocation ??
                              (entries[0].locationId || null);

                            const locationOptions = [
                              { value: "global", label: t("Select location") },
                              ...(Array.isArray(locations)
                                ? locations.map((loc: any) => ({
                                    value: loc?._id?.toString?.() || "",
                                    label: loc?.name || "",
                                  }))
                                : []),
                            ];
                            const showLocationSelect =
                              hasLocation || locationOptions.length > 0;

                            const selectedEntry =
                              rawEntries.find(
                                (e) =>
                                  (e.locationId || null) ===
                                  (selectedLoc || null),
                              ) ||
                              ({
                                supplierId: supId,
                                supplierName: entries[0]?.supplierName || "",
                                locationId: selectedLoc || null,
                                customValue: 0,
                                value: 0,
                                defaultValue: entries[0]?.defaultValue || 0,
                                totalValue: entries[0]?.defaultValue || 0,
                                excludeFromBusiness:
                                  entries[0]?.excludeFromBusiness || false,
                              } as any);

                            const isCollapsed = collapsedSuppliers[groupKey];
                            const disableCustomForGlobal =
                              !selectedEntry.locationId;

                            // Upozorenje: global entry sa custom entries (bug)
                            const isGlobalWithData =
                              !selectedEntry.locationId &&
                              (selectedEntry.customEntries?.length > 0 ||
                                selectedEntry.customValue > 0 ||
                                selectedEntry.dailyRevenueEntries?.length > 0);

                            const changeKey = supplierKey(
                              row.categoryId,
                              selectedEntry.supplierId,
                              selectedEntry.locationId || null,
                            );
                            const supplierChanged = hasSupplierChanges(
                              row.categoryId,
                              selectedEntry.supplierId,
                              selectedEntry.locationId || null,
                            );
                            const isSavingSupplier =
                              !!supplierSaving[changeKey];

                            return (
                              <div
                                key={groupKey}
                                style={{
                                  background: "#fff",
                                  border: supplierChanged
                                    ? "2px solid #faad14"
                                    : "1px solid #e0e0e0",
                                  boxShadow: supplierChanged
                                    ? "0 0 0 2px rgba(250, 173, 20, 0.15)"
                                    : "none",
                                  borderRadius: 6,
                                  marginBottom: 8,
                                  overflow: "hidden",
                                  transition: "all 0.2s ease",
                                }}
                              >
                                {/* SUPPLIER HEADER */}
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    justifyContent: "space-between",
                                    padding: "8px 12px",
                                    background: "#fafafa",
                                    cursor: "pointer",
                                    borderBottom: isCollapsed
                                      ? "none"
                                      : "1px solid #e0e0e0",
                                  }}
                                  onClick={() =>
                                    setCollapsedSuppliers((prev: any) => ({
                                      ...prev,
                                      [groupKey]: !prev[groupKey],
                                    }))
                                  }
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      gap: 8,
                                      alignItems: "center",
                                    }}
                                  >
                                    <Text strong>
                                      {selectedEntry.supplierName}
                                    </Text>
                                    {isGlobalWithData && (
                                      <Tooltip
                                        title={t(
                                          "Warning: This entry has no assigned location but contains data. Please assign a location and save.",
                                        )}
                                      >
                                        <Tag color="red" style={{ margin: 0 }}>
                                          {t("No location")}
                                        </Tag>
                                      </Tooltip>
                                    )}
                                    {showLocationSelect && (
                                      <Select
                                        size="small"
                                        value={selectedLoc || "global"}
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={(val) => {
                                          setSupplierEditLocation(
                                            (prev: any) => ({
                                              ...prev,
                                              [groupKey]:
                                                val === "global" ? null : val,
                                            }),
                                          );
                                        }}
                                        style={{ minWidth: 140 }}
                                      >
                                        {locationOptions.map((opt) => (
                                          <Option
                                            key={opt.value}
                                            value={opt.value}
                                          >
                                            {opt.label || t("All locations")}
                                          </Option>
                                        ))}
                                      </Select>
                                    )}
                                  </div>
                                  <div
                                    style={{
                                      display: "flex",
                                      gap: 10,
                                      alignItems: "center",
                                    }}
                                  >
                                    <Button
                                      size="small"
                                      icon={<SaveOutlined />}
                                      type="primary"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        saveSupplier(
                                          row.categoryId,
                                          selectedEntry.supplierId,
                                          selectedEntry.locationId || null,
                                        );
                                      }}
                                      disabled={!supplierChanged}
                                      loading={isSavingSupplier}
                                    >
                                      {t("Save")}
                                    </Button>

                                    <Button
                                      icon={
                                        isCollapsed ? (
                                          <DownOutlined />
                                        ) : (
                                          <UpOutlined />
                                        )
                                      }
                                      type="text"
                                      size="small"
                                    />
                                  </div>
                                </div>

                                {!isCollapsed && (
                                  <div
                                    style={{
                                      padding: "12px",
                                      background: "#fff",
                                    }}
                                  >
                                    {/* Pregled svih lokacija za ovog dobavljaca */}
                                    {entries.length > 1 && (
                                      <div
                                        style={{
                                          background: "#fafafa",
                                          padding: "8px 10px",
                                          borderRadius: 6,
                                          marginBottom: 12,
                                          border: "1px solid #e0e0e0",
                                        }}
                                      >
                                        <Text
                                          type="secondary"
                                          style={{
                                            fontSize: 12,
                                            fontWeight: 600,
                                            marginBottom: 8,
                                            display: "block",
                                          }}
                                        >
                                          {t("All locations")}:
                                        </Text>
                                        <div
                                          style={{
                                            display: "flex",
                                            gap: 8,
                                            flexWrap: "wrap",
                                          }}
                                        >
                                          {entries
                                            .filter((e) => e.locationId)
                                            .map((e) => (
                                              <Tag
                                                key={`${e.supplierId}-${
                                                  e.locationId || "global"
                                                }`}
                                              >
                                                {(e.locationId &&
                                                  locationMap[e.locationId]) ||
                                                  t("All locations")}
                                                :{" "}
                                                <strong>
                                                  {getDisplayedTotal(
                                                    row.categoryId,
                                                    e,
                                                  ).toLocaleString("sr-RS")}
                                                </strong>{" "}
                                                RSD
                                              </Tag>
                                            ))}
                                        </div>
                                      </div>
                                    )}

                                    {/* DEFAULT VALUE SECTION */}
                                    <div style={{ marginBottom: 12 }}>
                                      <div
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 6,
                                          marginBottom: 6,
                                        }}
                                      >
                                        <Text strong style={{ fontSize: 12 }}>
                                          {t("Default value")}
                                        </Text>
                                        <Tooltip
                                          title={t(
                                            "Base value from supplier history + expenses added via Daily Revenue this month",
                                          )}
                                        >
                                          <InfoCircleOutlined
                                            style={{
                                              color: "#8c8c8c",
                                              fontSize: 11,
                                            }}
                                          />
                                        </Tooltip>
                                      </div>
                                      <InputNumber
                                        min={0}
                                        value={getDisplayedDefault(
                                          row.categoryId,
                                          selectedEntry,
                                        )}
                                        disabled
                                        style={{
                                          width: "100%",
                                          borderRadius: 6,
                                        }}
                                        formatter={(value) =>
                                          `${value}`.replace(
                                            /\B(?=(\d{3})+(?!\d))/g,
                                            ",",
                                          )
                                        }
                                        suffix="RSD"
                                      />
                                      {(() => {
                                        const key = createDailyExpenseKey(
                                          row.categoryId,
                                          supId,
                                          selectedEntry.locationId,
                                        );
                                        const dailyData = dailyExpenseAgg[key];
                                        if (!dailyData) return null;
                                        return (
                                          <Tooltip
                                            title={renderDailyDetail(
                                              row.categoryId,
                                              supId,
                                              selectedEntry.locationId,
                                            )}
                                            overlayStyle={{ maxWidth: 400 }}
                                          >
                                            <Text
                                              type="secondary"
                                              style={{
                                                fontSize: 11,
                                                marginTop: 6,
                                                display: "block",
                                                cursor: "pointer",
                                                textDecoration:
                                                  "underline dotted",
                                              }}
                                            >
                                              {t("From Daily Revenue")}:{" "}
                                              <strong>
                                                {getDailyAdded(
                                                  row.categoryId,
                                                  selectedEntry,
                                                ).toLocaleString("sr-RS")}
                                              </strong>{" "}
                                              RSD ({dailyData.count}{" "}
                                              {t("entries")})
                                            </Text>
                                          </Tooltip>
                                        );
                                      })()}

                                      {/* Daily Revenue Entries Section */}
                                      {(() => {
                                        const key = createDailyExpenseKey(
                                          row.categoryId,
                                          supId,
                                          selectedEntry.locationId,
                                        );
                                        const detail = dailyExpenseDetail[key];
                                        const entries = Array.isArray(
                                          detail?.entries,
                                        )
                                          ? detail.entries
                                          : [];

                                        if (entries.length === 0) return null;

                                        return (
                                          <div style={{ marginTop: 12 }}>
                                            <div
                                              style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 6,
                                                marginBottom: 8,
                                              }}
                                            >
                                              <Text
                                                strong
                                                style={{ fontSize: 12 }}
                                              >
                                                {t("From Daily Revenue")}
                                              </Text>
                                              <Tooltip
                                                title={t(
                                                  "Expenses added via Daily Revenue this month",
                                                )}
                                              >
                                                <InfoCircleOutlined
                                                  style={{
                                                    color: "#8c8c8c",
                                                    fontSize: 11,
                                                  }}
                                                />
                                              </Tooltip>
                                            </div>

                                            <div
                                              style={{
                                                display: "flex",
                                                flexDirection: "column",
                                                gap: 6,
                                                marginBottom: 6,
                                              }}
                                            >
                                              {entries.map(
                                                (entry: any, idx: number) => {
                                                  const entryStatus =
                                                    entry.paymentStatus ||
                                                    "unpaid";
                                                  const statusColor =
                                                    entryStatus === "paid"
                                                      ? "#52c41a"
                                                      : entryStatus ===
                                                          "partial"
                                                        ? "#faad14"
                                                        : "#ff4d4f";

                                                  return (
                                                    <div
                                                      key={idx}
                                                      style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent:
                                                          "space-between",
                                                        padding: "6px 10px",
                                                        background: "#fafafa",
                                                        borderRadius: 4,
                                                        border:
                                                          "1px solid #f0f0f0",
                                                      }}
                                                    >
                                                      <div
                                                        style={{
                                                          display: "flex",
                                                          alignItems: "center",
                                                          gap: 8,
                                                          flex: 1,
                                                        }}
                                                      >
                                                        <div
                                                          onClick={() =>
                                                            handleDailyRevenuePaymentClick(
                                                              entry,
                                                              row.categoryId,
                                                              selectedEntry.supplierId,
                                                              selectedEntry.locationId,
                                                            )
                                                          }
                                                          style={{
                                                            width: "16px",
                                                            height: "16px",
                                                            borderRadius: "50%",
                                                            backgroundColor:
                                                              statusColor,
                                                            cursor: "pointer",
                                                            border:
                                                              "2px solid #fff",
                                                            boxShadow:
                                                              "0 2px 4px rgba(0,0,0,0.1)",
                                                            flexShrink: 0,
                                                          }}
                                                          title={
                                                            entryStatus ===
                                                            "paid"
                                                              ? t(
                                                                  "expensesMatrix.paid",
                                                                )
                                                              : entryStatus ===
                                                                  "partial"
                                                                ? `${t(
                                                                    "expensesMatrix.partial",
                                                                  )}: ${
                                                                    entry
                                                                      .partialPayment
                                                                      ?.paidAmount ||
                                                                    0
                                                                  }/${
                                                                    entry.amount
                                                                  } RSD`
                                                                : t(
                                                                    "expensesMatrix.unpaid",
                                                                  )
                                                          }
                                                        />
                                                        <Text
                                                          style={{
                                                            fontSize: 11,
                                                            color: "#595959",
                                                          }}
                                                        >
                                                          {dayjs(
                                                            entry.date,
                                                          ).format(
                                                            "DD.MM.YYYY",
                                                          )}
                                                          {entry.location && (
                                                            <span
                                                              style={{
                                                                color:
                                                                  "#8c8c8c",
                                                              }}
                                                            >
                                                              {" "}
                                                              · {entry.location}
                                                            </span>
                                                          )}
                                                        </Text>
                                                      </div>
                                                      <Text
                                                        style={{
                                                          fontSize: 11,
                                                          fontWeight: 500,
                                                        }}
                                                      >
                                                        {Number(
                                                          entry.amount || 0,
                                                        ).toLocaleString(
                                                          "sr-RS",
                                                        )}{" "}
                                                        RSD
                                                      </Text>
                                                    </div>
                                                  );
                                                },
                                              )}
                                            </div>
                                          </div>
                                        );
                                      })()}
                                    </div>

                                    {/* BASE EXPENSE SECTION (only show if there's baseline, not just daily revenue) */}
                                    {(() => {
                                      const defaultVal =
                                        selectedEntry.defaultValue || 0;
                                      const dailyTotal = getDailyAdded(
                                        row.categoryId,
                                        selectedEntry,
                                      );
                                      const hasBaseline =
                                        defaultVal > dailyTotal; // Has more than just daily revenue

                                      if (!hasBaseline) return null;

                                      return (
                                        <div style={{ marginBottom: 16 }}>
                                          <div
                                            style={{
                                              display: "flex",
                                              alignItems: "center",
                                              gap: 6,
                                              marginBottom: 6,
                                            }}
                                          >
                                            <Text
                                              strong
                                              style={{ fontSize: 12 }}
                                            >
                                              {t("Base monthly expense")}
                                            </Text>
                                            <Tooltip
                                              title={t(
                                                "Includes fixed monthly costs and daily revenue expenses",
                                              )}
                                            >
                                              <InfoCircleOutlined
                                                style={{
                                                  color: "#8c8c8c",
                                                  fontSize: 11,
                                                }}
                                              />
                                            </Tooltip>
                                          </div>

                                          <div
                                            style={{
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "space-between",
                                              padding: "8px 12px",
                                              background: "#f0f5ff",
                                              borderRadius: 6,
                                              border: "1px solid #d6e4ff",
                                            }}
                                          >
                                            <div
                                              style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 10,
                                                flex: 1,
                                              }}
                                            >
                                              {/* Payment Status Circles */}
                                              <div
                                                style={{
                                                  display: "flex",
                                                  gap: 6,
                                                  alignItems: "center",
                                                }}
                                              >
                                                {[
                                                  "paid",
                                                  "unpaid",
                                                  "partial",
                                                ].map((status) => {
                                                  const currentStatus =
                                                    selectedEntry.defaultPaymentStatus ||
                                                    "unpaid";
                                                  const isActive =
                                                    currentStatus === status;
                                                  const isLocationSelected =
                                                    selectedLoc &&
                                                    selectedLoc !== "global";
                                                  const color =
                                                    status === "paid"
                                                      ? "#52c41a"
                                                      : status === "partial"
                                                        ? "#faad14"
                                                        : "#ff4d4f";
                                                  const grayColor = "#d9d9d9";

                                                  return (
                                                    <div
                                                      key={status}
                                                      onClick={() => {
                                                        if (
                                                          !canUpdateMatrix ||
                                                          !isLocationSelected
                                                        )
                                                          return;

                                                        if (
                                                          status === "partial"
                                                        ) {
                                                          // Open partial payment modal
                                                          setCustomEntryPartialModal(
                                                            {
                                                              visible: true,
                                                              categoryId:
                                                                row.categoryId,
                                                              supplierId:
                                                                selectedEntry.supplierId,
                                                              locationId:
                                                                selectedLoc ||
                                                                null,
                                                              entryIndex: -1, // -1 = default value
                                                              totalAmount:
                                                                selectedEntry.defaultValue,
                                                              paidAmount:
                                                                selectedEntry
                                                                  .defaultPartialPayment
                                                                  ?.paidAmount ||
                                                                0,
                                                              remainingAmount:
                                                                selectedEntry
                                                                  .defaultPartialPayment
                                                                  ?.remainingAmount ||
                                                                selectedEntry.defaultValue,
                                                            },
                                                          );
                                                        } else {
                                                          // Update default payment status directly
                                                          handleQuickDefaultValueStatusChange(
                                                            {
                                                              categoryId:
                                                                row.categoryId,
                                                              supplierId:
                                                                selectedEntry.supplierId,
                                                              locationId:
                                                                selectedLoc ||
                                                                null,
                                                              amount:
                                                                selectedEntry.defaultValue,
                                                            },
                                                            status,
                                                          );
                                                        }
                                                      }}
                                                      style={{
                                                        width: "16px",
                                                        height: "16px",
                                                        borderRadius: "50%",
                                                        backgroundColor:
                                                          !isLocationSelected
                                                            ? "#f5f5f5"
                                                            : isActive
                                                              ? color
                                                              : "transparent",
                                                        border: `2px solid ${
                                                          !isLocationSelected
                                                            ? grayColor
                                                            : isActive
                                                              ? color
                                                              : "#d9d9d9"
                                                        }`,
                                                        cursor:
                                                          canUpdateMatrix &&
                                                          isLocationSelected
                                                            ? "pointer"
                                                            : "not-allowed",
                                                        transition: "all 0.2s",
                                                        opacity:
                                                          canUpdateMatrix &&
                                                          isLocationSelected
                                                            ? 1
                                                            : 0.4,
                                                      }}
                                                      title={
                                                        !isLocationSelected
                                                          ? t(
                                                              "Select location first",
                                                            )
                                                          : t(
                                                              `expensesMatrix.${status}`,
                                                            )
                                                      }
                                                    />
                                                  );
                                                })}
                                              </div>

                                              <Text
                                                style={{
                                                  fontSize: 12,
                                                  color: "#262626",
                                                }}
                                              >
                                                {t("Base monthly expense")}
                                              </Text>
                                            </div>

                                            <Text
                                              strong
                                              style={{
                                                fontSize: 13,
                                                color: "#1890ff",
                                              }}
                                            >
                                              {Number(
                                                selectedEntry.defaultValue || 0,
                                              ).toLocaleString("sr-RS")}{" "}
                                              RSD
                                            </Text>
                                          </div>

                                          {selectedEntry.defaultPaymentStatus ===
                                            "partial" &&
                                            selectedEntry.defaultPartialPayment && (
                                              <div
                                                style={{
                                                  marginTop: 6,
                                                  padding: "6px 12px",
                                                  background: "#fffbe6",
                                                  borderRadius: 4,
                                                  border: "1px solid #ffe58f",
                                                }}
                                              >
                                                <Text
                                                  style={{
                                                    fontSize: 11,
                                                    color: "#8c8c8c",
                                                  }}
                                                >
                                                  {t("Paid")}:{" "}
                                                  <strong>
                                                    {Number(
                                                      selectedEntry
                                                        .defaultPartialPayment
                                                        ?.paidAmount || 0,
                                                    ).toLocaleString("sr-RS")}
                                                  </strong>{" "}
                                                  RSD | {t("Remaining")}:{" "}
                                                  <strong>
                                                    {Number(
                                                      selectedEntry
                                                        .defaultPartialPayment
                                                        ?.remainingAmount || 0,
                                                    ).toLocaleString("sr-RS")}
                                                  </strong>{" "}
                                                  RSD
                                                </Text>
                                              </div>
                                            )}
                                        </div>
                                      );
                                    })()}

                                    {/* CUSTOM ENTRIES SECTION - Only show if supplier has location */}
                                    {selectedEntry.locationId && (
                                      <div>
                                        <div
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 6,
                                            marginBottom: 6,
                                          }}
                                        >
                                          <Text strong style={{ fontSize: 12 }}>
                                            {t("Custom entries")}
                                          </Text>
                                          <Tooltip
                                            title={t(
                                              "Manually added values for this month",
                                            )}
                                          >
                                            <InfoCircleOutlined
                                              style={{
                                                color: "#8c8c8c",
                                                fontSize: 11,
                                              }}
                                            />
                                          </Tooltip>
                                        </div>

                                        <div
                                          style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: 6,
                                            marginBottom: 6,
                                          }}
                                        >
                                          {Array.isArray(
                                            selectedEntry.customEntries,
                                          ) &&
                                            selectedEntry.customEntries.map(
                                              (ce: any, idx: number) => {
                                                const entryKey = `${
                                                  row.categoryId
                                                }-${selectedEntry.supplierId}-${
                                                  selectedLoc || "global"
                                                }-${idx}`;
                                                const currentDate =
                                                  customEntryDates[entryKey] ||
                                                  parseDateFromName(
                                                    ce.description || "",
                                                  );
                                                return (
                                                  <div
                                                    key={`${selectedEntry.supplierId}-${idx}`}
                                                    style={{
                                                      display: "flex",
                                                      gap: 6,
                                                      background: "#fafafa",
                                                      padding: 8,
                                                      borderRadius: 6,
                                                      border:
                                                        "1px solid #e0e0e0",
                                                      alignItems: "flex-start",
                                                    }}
                                                  >
                                                    <Input
                                                      placeholder={t(
                                                        "Description",
                                                      )}
                                                      value={ce.description}
                                                      onChange={(e) => {
                                                        const nextEntries = [
                                                          ...(selectedEntry.customEntries ||
                                                            []),
                                                        ];
                                                        nextEntries[idx] = {
                                                          ...nextEntries[idx],
                                                          description:
                                                            e.target.value,
                                                        };
                                                        updateValue(
                                                          row.categoryId,
                                                          selectedEntry.supplierId,
                                                          selectedLoc || null,
                                                          nextEntries,
                                                        );
                                                      }}
                                                      disabled={
                                                        !canUpdateMatrix ||
                                                        disableCustomForGlobal
                                                      }
                                                      style={{ flex: 1 }}
                                                    />
                                                    <div
                                                      style={{
                                                        position: "relative",
                                                        width: 32,
                                                        height: 32,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent:
                                                          "center",
                                                      }}
                                                    >
                                                      <DatePicker
                                                        open={
                                                          openDateKey ===
                                                          entryKey
                                                        }
                                                        onOpenChange={(
                                                          status,
                                                        ) =>
                                                          setOpenDateKey(
                                                            status
                                                              ? entryKey
                                                              : null,
                                                          )
                                                        }
                                                        size="small"
                                                        allowClear={false}
                                                        value={
                                                          currentDate
                                                            ? dayjs(
                                                                currentDate,
                                                                "DD.MM.YYYY",
                                                              )
                                                            : null
                                                        }
                                                        inputReadOnly
                                                        suffixIcon={null}
                                                        bordered={false}
                                                        format={() => ""}
                                                        style={{
                                                          position: "absolute",
                                                          inset: 0,
                                                          width: 32,
                                                          height: 32,
                                                          opacity: 0,
                                                          pointerEvents: "none",
                                                        }}
                                                        onChange={(date) => {
                                                          if (
                                                            !canUpdateMatrix ||
                                                            disableCustomForGlobal
                                                          )
                                                            return;
                                                          const formatted =
                                                            formatCustomDate(
                                                              date,
                                                            );
                                                          const nextEntries = [
                                                            ...(selectedEntry.customEntries ||
                                                              []),
                                                          ];
                                                          const nextEntry = {
                                                            ...(nextEntries[
                                                              idx
                                                            ] || {}),
                                                          };
                                                          nextEntry.description =
                                                            formatted
                                                              ? upsertDateIntoName(
                                                                  nextEntry.description ||
                                                                    "",
                                                                  formatted,
                                                                )
                                                              : removeDateFromName(
                                                                  nextEntry.description ||
                                                                    "",
                                                                );
                                                          nextEntries[idx] =
                                                            nextEntry;
                                                          setCustomEntryDates(
                                                            (prev) => ({
                                                              ...prev,
                                                              [entryKey]:
                                                                formatted ||
                                                                undefined,
                                                            }),
                                                          );
                                                          updateValue(
                                                            row.categoryId,
                                                            selectedEntry.supplierId,
                                                            selectedLoc || null,
                                                            nextEntries,
                                                          );
                                                          setOpenDateKey(null);
                                                        }}
                                                        disabled={
                                                          !canUpdateMatrix ||
                                                          disableCustomForGlobal
                                                        }
                                                      />
                                                      <Button
                                                        size="small"
                                                        type="text"
                                                        icon={
                                                          currentDate ? (
                                                            <CloseCircleOutlined
                                                              style={{
                                                                color:
                                                                  "#ff4d4f",
                                                              }}
                                                            />
                                                          ) : (
                                                            <CalendarOutlined />
                                                          )
                                                        }
                                                        onClick={() => {
                                                          if (
                                                            !canUpdateMatrix ||
                                                            disableCustomForGlobal
                                                          )
                                                            return;
                                                          if (currentDate) {
                                                            const nextEntries =
                                                              [
                                                                ...(selectedEntry.customEntries ||
                                                                  []),
                                                              ];
                                                            const nextEntry = {
                                                              ...(nextEntries[
                                                                idx
                                                              ] || {}),
                                                            };
                                                            nextEntry.description =
                                                              removeDateFromName(
                                                                nextEntry.description ||
                                                                  "",
                                                              );
                                                            nextEntries[idx] =
                                                              nextEntry;
                                                            setCustomEntryDates(
                                                              (prev) => {
                                                                const copy = {
                                                                  ...prev,
                                                                };
                                                                delete copy[
                                                                  entryKey
                                                                ];
                                                                return copy;
                                                              },
                                                            );
                                                            updateValue(
                                                              row.categoryId,
                                                              selectedEntry.supplierId,
                                                              selectedLoc ||
                                                                null,
                                                              nextEntries,
                                                            );
                                                            setOpenDateKey(
                                                              null,
                                                            );
                                                          } else {
                                                            setOpenDateKey(
                                                              entryKey,
                                                            );
                                                          }
                                                        }}
                                                        disabled={
                                                          !canUpdateMatrix ||
                                                          disableCustomForGlobal
                                                        }
                                                        style={{
                                                          width: 32,
                                                          height: 32,
                                                        }}
                                                      />
                                                    </div>
                                                    <InputNumber
                                                      min={0}
                                                      placeholder="0"
                                                      value={Number(
                                                        ce.amount || 0,
                                                      )}
                                                      onChange={(v) => {
                                                        const nextEntries = [
                                                          ...(selectedEntry.customEntries ||
                                                            []),
                                                        ];
                                                        nextEntries[idx] = {
                                                          ...nextEntries[idx],
                                                          amount: Math.max(
                                                            0,
                                                            Number(v || 0),
                                                          ),
                                                        };
                                                        updateValue(
                                                          row.categoryId,
                                                          selectedEntry.supplierId,
                                                          selectedLoc || null,
                                                          nextEntries,
                                                        );
                                                      }}
                                                      style={{ width: 140 }}
                                                      disabled={
                                                        !canUpdateMatrix ||
                                                        disableCustomForGlobal
                                                      }
                                                      formatter={(value) =>
                                                        `${value}`.replace(
                                                          /\B(?=(\d{3})+(?!\d))/g,
                                                          ",",
                                                        )
                                                      }
                                                      suffix="RSD"
                                                    />

                                                    {/* Payment Status Circles for Custom Entry */}
                                                    {canUpdateMatrix &&
                                                      !disableCustomForGlobal && (
                                                        <div
                                                          style={{
                                                            display: "flex",
                                                            gap: 4,
                                                          }}
                                                        >
                                                          {[
                                                            {
                                                              value: "paid",
                                                              color: "#52c41a",
                                                            },
                                                            {
                                                              value: "partial",
                                                              color: "#faad14",
                                                            },
                                                            {
                                                              value: "unpaid",
                                                              color: "#ff4d4f",
                                                            },
                                                          ].map((opt) => {
                                                            const active =
                                                              (ce.paymentStatus ||
                                                                "unpaid") ===
                                                              opt.value;
                                                            return (
                                                              <Tooltip
                                                                key={opt.value}
                                                                title={t(
                                                                  opt.value ===
                                                                    "paid"
                                                                    ? "Plaćeno"
                                                                    : opt.value ===
                                                                        "partial"
                                                                      ? "Delimično plaćeno"
                                                                      : "Neplaćeno",
                                                                )}
                                                              >
                                                                <div
                                                                  onClick={() => {
                                                                    const amount =
                                                                      Number(
                                                                        ce.amount ||
                                                                          0,
                                                                      );

                                                                    // Each circle directly sets its status
                                                                    if (
                                                                      opt.value ===
                                                                      "partial"
                                                                    ) {
                                                                      // Open modal for partial
                                                                      const existingPartial =
                                                                        ce.partialPayment ||
                                                                        {};
                                                                      const currentStatus =
                                                                        ce.paymentStatus ||
                                                                        "unpaid";

                                                                      // Set better initial values based on current status
                                                                      let initialPaid =
                                                                        existingPartial.paidAmount ||
                                                                        0;
                                                                      let initialRemaining =
                                                                        existingPartial.remainingAmount ||
                                                                        0;

                                                                      // If coming from paid/unpaid status, set sensible defaults
                                                                      if (
                                                                        currentStatus !==
                                                                        "partial"
                                                                      ) {
                                                                        if (
                                                                          currentStatus ===
                                                                          "paid"
                                                                        ) {
                                                                          initialPaid =
                                                                            amount;
                                                                          initialRemaining = 0;
                                                                        } else {
                                                                          initialPaid = 0;
                                                                          initialRemaining =
                                                                            amount;
                                                                        }
                                                                      }

                                                                      setCustomEntryPartialModal(
                                                                        {
                                                                          visible: true,
                                                                          categoryId:
                                                                            row.categoryId,
                                                                          supplierId:
                                                                            selectedEntry.supplierId,
                                                                          locationId:
                                                                            selectedLoc ||
                                                                            null,
                                                                          entryIndex:
                                                                            idx,
                                                                          totalAmount:
                                                                            amount,
                                                                          paidAmount:
                                                                            initialPaid,
                                                                          remainingAmount:
                                                                            initialRemaining,
                                                                        },
                                                                      );
                                                                    } else if (
                                                                      opt.value ===
                                                                      "paid"
                                                                    ) {
                                                                      // Set to paid
                                                                      const nextEntries =
                                                                        [
                                                                          ...(selectedEntry.customEntries ||
                                                                            []),
                                                                        ];
                                                                      nextEntries[
                                                                        idx
                                                                      ] = {
                                                                        ...nextEntries[
                                                                          idx
                                                                        ],
                                                                        paymentStatus:
                                                                          "paid",
                                                                        partialPayment:
                                                                          {
                                                                            paidAmount:
                                                                              amount,
                                                                            remainingAmount: 0,
                                                                          },
                                                                      };
                                                                      updateValue(
                                                                        row.categoryId,
                                                                        selectedEntry.supplierId,
                                                                        selectedLoc ||
                                                                          null,
                                                                        nextEntries,
                                                                      );
                                                                    } else if (
                                                                      opt.value ===
                                                                      "unpaid"
                                                                    ) {
                                                                      // Set to unpaid
                                                                      const nextEntries =
                                                                        [
                                                                          ...(selectedEntry.customEntries ||
                                                                            []),
                                                                        ];
                                                                      nextEntries[
                                                                        idx
                                                                      ] = {
                                                                        ...nextEntries[
                                                                          idx
                                                                        ],
                                                                        paymentStatus:
                                                                          "unpaid",
                                                                        partialPayment:
                                                                          {
                                                                            paidAmount: 0,
                                                                            remainingAmount:
                                                                              amount,
                                                                          },
                                                                      };
                                                                      updateValue(
                                                                        row.categoryId,
                                                                        selectedEntry.supplierId,
                                                                        selectedLoc ||
                                                                          null,
                                                                        nextEntries,
                                                                      );
                                                                    }
                                                                  }}
                                                                  style={{
                                                                    width: 18,
                                                                    height: 18,
                                                                    borderRadius:
                                                                      "50%",
                                                                    border: `2px solid ${opt.color}`,
                                                                    background:
                                                                      active
                                                                        ? opt.color
                                                                        : "transparent",
                                                                    cursor:
                                                                      "pointer",
                                                                    display:
                                                                      "inline-flex",
                                                                    alignItems:
                                                                      "center",
                                                                    justifyContent:
                                                                      "center",
                                                                    transition:
                                                                      "all 0.2s",
                                                                  }}
                                                                >
                                                                  {active && (
                                                                    <span
                                                                      style={{
                                                                        width: 6,
                                                                        height: 6,
                                                                        background:
                                                                          "#fff",
                                                                        borderRadius:
                                                                          "50%",
                                                                      }}
                                                                    />
                                                                  )}
                                                                </div>
                                                              </Tooltip>
                                                            );
                                                          })}
                                                        </div>
                                                      )}

                                                    {canUpdateMatrix && (
                                                      <Button
                                                        type="text"
                                                        danger
                                                        size="small"
                                                        icon={
                                                          <DeleteOutlined />
                                                        }
                                                        onClick={() => {
                                                          const nextEntries = (
                                                            selectedEntry.customEntries ||
                                                            []
                                                          ).filter(
                                                            (
                                                              _: any,
                                                              i: number,
                                                            ) => i !== idx,
                                                          );
                                                          setCustomEntryDates(
                                                            (prev) => {
                                                              const copy = {
                                                                ...prev,
                                                              };
                                                              delete copy[
                                                                entryKey
                                                              ];
                                                              return copy;
                                                            },
                                                          );
                                                          updateValue(
                                                            row.categoryId,
                                                            selectedEntry.supplierId,
                                                            selectedLoc || null,
                                                            nextEntries,
                                                          );
                                                        }}
                                                      />
                                                    )}
                                                  </div>
                                                );
                                              },
                                            )}
                                        </div>

                                        {canUpdateMatrix && (
                                          <Button
                                            type="dashed"
                                            size="small"
                                            icon={<PlusOutlined />}
                                            onClick={() =>
                                              updateValue(
                                                row.categoryId,
                                                selectedEntry.supplierId,
                                                selectedLoc || null,
                                                [
                                                  ...(selectedEntry.customEntries ||
                                                    []),
                                                  {
                                                    description: "",
                                                    amount: 0,
                                                  },
                                                ],
                                              )
                                            }
                                            style={{
                                              width: "100%",
                                              borderRadius: 6,
                                            }}
                                            disabled={disableCustomForGlobal}
                                          >
                                            {t("Add custom entry")}
                                          </Button>
                                        )}

                                        {(!selectedEntry.customEntries ||
                                          selectedEntry.customEntries.length ===
                                            0) && (
                                          <Text
                                            type="secondary"
                                            style={{
                                              fontSize: 11,
                                              textAlign: "center",
                                              display: "block",
                                              padding: "8px 0",
                                            }}
                                          >
                                            {t("No custom entries")}
                                          </Text>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          },
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </Card>
      )}

      {/* NEW CUSTOM VALUES SECTION */}
      {false && (isSuperAdmin(userData?.roles) || canViewExpenses) && (
        <Card
          className="exp-panel"
          bordered={false}
          style={{ marginTop: 24 }}
          loading={customValuesLoading}
        >
          <div style={{ marginBottom: 16 }}>
            <Title level={4}>{t("Custom Supplier Values")}</Title>
            <Text type="secondary">
              {t(
                "Add custom values for suppliers by location for the selected month. Includes expenses from Daily Revenue (new system).",
              )}
            </Text>
          </div>

          {matrix && categories && categories.length > 0 && (
            <div>
              {categories.map((category: any) => {
                if (!category.suppliers || category.suppliers.length === 0)
                  return null;

                return (
                  <div key={category._id} style={{ marginBottom: 24 }}>
                    <Divider orientation="left">
                      <Text strong>{category.name}</Text>
                    </Divider>

                    {category.suppliers.map((supplier: any) => {
                      // Get all locations for this supplier from matrix
                      const matrixLocations = availableLocations.filter(
                        (loc: any) => {
                          return matrix.rows.some((row: any) =>
                            row.suppliers.some(
                              (s: any) =>
                                s.supplierId === supplier._id &&
                                s.locationId?.toString() === loc._id,
                            ),
                          );
                        },
                      );

                      // Get all locations for this supplier from custom values
                      const customValueLocations = customValues
                        .filter((cv) => cv.supplierId === supplier._id)
                        .map((cv) => cv.locationId)
                        .filter((locId: any, index: number, self: any[]) => {
                          // Unique locations only
                          const locIdStr =
                            typeof locId === "string" ? locId : locId._id;
                          return (
                            self.findIndex((l: any) => {
                              const lStr = typeof l === "string" ? l : l._id;
                              return lStr === locIdStr;
                            }) === index
                          );
                        })
                        .map((locId: any) => {
                          const locIdStr =
                            typeof locId === "string" ? locId : locId._id;
                          return availableLocations.find(
                            (loc: any) => loc._id === locIdStr,
                          );
                        })
                        .filter(Boolean);

                      // Get all locations for this supplier from daily revenue expenses
                      const dailyRevenueLocations = dailyRevenueExpenses
                        .filter((exp) => exp.supplierId === supplier._id)
                        .map((exp) => exp.locationId)
                        .filter((locId: any, index: number, self: any[]) => {
                          // Unique locations only
                          const locIdStr =
                            typeof locId === "string" ? locId : locId._id;
                          return (
                            self.findIndex((l: any) => {
                              const lStr = typeof l === "string" ? l : l._id;
                              return lStr === locIdStr;
                            }) === index
                          );
                        })
                        .map((locId: any) => {
                          const locIdStr =
                            typeof locId === "string" ? locId : locId._id;
                          return availableLocations.find(
                            (loc: any) => loc._id === locIdStr,
                          );
                        })
                        .filter(Boolean);

                      // Combine all location sets (unique)
                      const allLocationIds = new Set([
                        ...matrixLocations.map((l: any) => l._id),
                        ...customValueLocations.map((l: any) => l._id),
                        ...dailyRevenueLocations.map((l: any) => l._id),
                      ]);

                      const supplierLocations = Array.from(allLocationIds)
                        .map((id) =>
                          availableLocations.find((loc: any) => loc._id === id),
                        )
                        .filter(Boolean);

                      if (supplierLocations.length === 0) return null;

                      return (
                        <div
                          key={supplier._id}
                          style={{
                            marginBottom: 16,
                            border: "1px solid #d9d9d9",
                            borderRadius: 8,
                            padding: 16,
                          }}
                        >
                          <Text
                            strong
                            style={{
                              fontSize: 16,
                              marginBottom: 12,
                              display: "block",
                            }}
                          >
                            {supplier.name}
                          </Text>

                          <Row gutter={[16, 16]}>
                            {supplierLocations.map((location: any) => {
                              // Get custom values for this supplier + location + month
                              const locationCustomValues = customValues.filter(
                                (cv: any) => {
                                  const cvLocationId =
                                    typeof cv.locationId === "string"
                                      ? cv.locationId
                                      : cv.locationId?._id;
                                  return (
                                    cv.supplierId === supplier._id &&
                                    cvLocationId === location._id
                                  );
                                },
                              );

                              // Get daily revenue expenses for this supplier + location + month
                              const locationDailyRevenueExpenses =
                                dailyRevenueExpenses.filter((exp: any) => {
                                  const expLocationId =
                                    typeof exp.locationId === "string"
                                      ? exp.locationId
                                      : exp.locationId?._id;
                                  return (
                                    exp.supplierId === supplier._id &&
                                    expLocationId === location._id &&
                                    !exp.other // Exclude "other" expenses from business view
                                  );
                                });

                              // Combine both custom values and daily revenue expenses
                              const allExpenses = [
                                ...locationCustomValues,
                                ...locationDailyRevenueExpenses,
                              ];

                              // Get fixed monthly cost from matrix
                              let fixedMonthlyCost = 0;
                              const matrixRow = matrix.rows.find(
                                (row: any) => row.categoryId === category._id,
                              );
                              if (matrixRow) {
                                const matrixSupplier = matrixRow.suppliers.find(
                                  (s: any) =>
                                    s.supplierId === supplier._id &&
                                    s.locationId?.toString() === location._id,
                                );
                                if (matrixSupplier) {
                                  fixedMonthlyCost =
                                    matrixSupplier.defaultValue || 0;
                                }
                              }

                              // Group by currency for totals
                              const totalsByCurrency = allExpenses.reduce(
                                (acc, item: any) => {
                                  // DailyRevenueSupplierExpense doesn't have currency field, always RSD
                                  const curr = item.currency || "RSD";
                                  acc[curr] = (acc[curr] || 0) + item.amount;
                                  return acc;
                                },
                                {} as Record<string, number>,
                              );

                              const hasTotals =
                                Object.keys(totalsByCurrency).length > 0;

                              return (
                                <Col xs={24} md={12} lg={8} key={location._id}>
                                  <Card
                                    size="small"
                                    title={location.name}
                                    extra={
                                      canUpdateMatrix && (
                                        <Button
                                          type="link"
                                          size="small"
                                          icon={<PlusOutlined />}
                                          onClick={() => {
                                            setSelectedSupplierForCustomValue({
                                              supplierId: supplier._id,
                                              supplierName: supplier.name,
                                              categoryId: category._id,
                                              locationId: location._id,
                                            });
                                            setCustomValueModalVisible(true);
                                          }}
                                        >
                                          {t("Add")}
                                        </Button>
                                      )
                                    }
                                    style={{
                                      borderRadius: 8,
                                      backgroundColor: hasTotals
                                        ? "#f6ffed"
                                        : "#fff",
                                    }}
                                  >
                                    {/* Fixed Monthly Cost Section */}
                                    {fixedMonthlyCost > 0 && (
                                      <div
                                        style={{
                                          marginBottom: 12,
                                          padding: 12,
                                          backgroundColor: "#e6f7ff",
                                          borderRadius: 6,
                                          border: "1px solid #91d5ff",
                                        }}
                                      >
                                        <div
                                          style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                          }}
                                        >
                                          <Text
                                            type="secondary"
                                            style={{ fontSize: 12 }}
                                          >
                                            {t("Fixed monthly cost")}
                                          </Text>
                                          <Text
                                            strong
                                            style={{
                                              fontSize: 14,
                                              color: "#1890ff",
                                            }}
                                          >
                                            {fixedMonthlyCost.toLocaleString(
                                              "sr-RS",
                                            )}{" "}
                                            RSD
                                          </Text>
                                        </div>
                                      </div>
                                    )}

                                    {/* Custom Values and Daily Revenue Expenses List */}
                                    {allExpenses.length > 0 ? (
                                      <List
                                        size="small"
                                        dataSource={allExpenses}
                                        renderItem={(item: any) => {
                                          // Check if this is from custom values or daily revenue
                                          const isCustomValue =
                                            customValues.some(
                                              (cv) => cv._id === item._id,
                                            );
                                          const isDailyRevenueExpense =
                                            dailyRevenueExpenses.some(
                                              (exp) => exp._id === item._id,
                                            );

                                          return (
                                            <List.Item
                                              actions={
                                                canUpdateMatrix && isCustomValue
                                                  ? [
                                                      <Button
                                                        key="delete"
                                                        type="text"
                                                        danger
                                                        size="small"
                                                        icon={
                                                          <DeleteOutlined />
                                                        }
                                                        onClick={() =>
                                                          handleDeleteCustomValue(
                                                            item._id,
                                                          )
                                                        }
                                                      />,
                                                    ]
                                                  : undefined
                                              }
                                            >
                                              <List.Item.Meta
                                                description={
                                                  <div>
                                                    <div
                                                      style={{
                                                        marginBottom: 4,
                                                      }}
                                                    >
                                                      {item.description ||
                                                        t("No description")}
                                                      {isDailyRevenueExpense && (
                                                        <Tag
                                                          color="blue"
                                                          style={{
                                                            marginLeft: 8,
                                                          }}
                                                        >
                                                          {t("Daily Revenue")}
                                                        </Tag>
                                                      )}
                                                    </div>
                                                    {isCustomValue &&
                                                      (item.invoiceDate ||
                                                        item.createdAt) && (
                                                        <div
                                                          style={{
                                                            marginBottom: 4,
                                                          }}
                                                        >
                                                          <Text
                                                            type="secondary"
                                                            style={{
                                                              fontSize: 12,
                                                            }}
                                                          >
                                                            {item.invoiceDate
                                                              ? `${t("Invoice Date")}: ${dayjs(item.invoiceDate).format("DD.MM.YYYY")}`
                                                              : `${t("Created")}: ${dayjs(item.createdAt).format("DD.MM.YYYY")}`}
                                                          </Text>
                                                        </div>
                                                      )}
                                                    <Text
                                                      strong
                                                      style={{
                                                        color: "#52c41a",
                                                      }}
                                                    >
                                                      {item.amount.toLocaleString(
                                                        "sr-RS",
                                                      )}{" "}
                                                      {item.currency || "RSD"}
                                                    </Text>
                                                  </div>
                                                }
                                              />
                                            </List.Item>
                                          );
                                        }}
                                      />
                                    ) : (
                                      <Text type="secondary">
                                        {t("No expenses")}
                                      </Text>
                                    )}

                                    {hasTotals && (
                                      <div
                                        style={{
                                          marginTop: 8,
                                          paddingTop: 8,
                                          borderTop: "1px solid #d9d9d9",
                                        }}
                                      >
                                        <Text
                                          strong
                                          style={{
                                            display: "block",
                                            marginBottom: 4,
                                          }}
                                        >
                                          {t("Total")}:
                                        </Text>
                                        {Object.entries(totalsByCurrency).map(
                                          ([currency, total]) => (
                                            <div
                                              key={currency}
                                              style={{ marginBottom: 4 }}
                                            >
                                              <Text
                                                strong
                                                style={{
                                                  fontSize: 16,
                                                  color: "#52c41a",
                                                }}
                                              >
                                                {total.toLocaleString("sr-RS")}{" "}
                                                {currency}
                                              </Text>
                                            </div>
                                          ),
                                        )}
                                      </div>
                                    )}
                                  </Card>
                                </Col>
                              );
                            })}
                          </Row>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      )}

      {/* PARTIAL PAYMENT MODAL */}
      <Modal
        title={t("Partial Payment Details")}
        open={partialPaymentModal.visible}
        onOk={handlePartialPaymentConfirm}
        className="standardHeader standardFooter"
        onCancel={handlePartialPaymentCancel}
        okText={t("Confirm")}
        cancelText={t("Cancel")}
        width={500}
      >
        {(() => {
          // Get current totalValue from matrix in real-time
          let currentTotal = partialPaymentModal.totalAmount;
          if (
            matrix &&
            partialPaymentModal.categoryId &&
            partialPaymentModal.supplierId
          ) {
            const row = matrix.rows.find(
              (r: any) => r.categoryId === partialPaymentModal.categoryId,
            );
            if (row) {
              const supplier = row.suppliers.find(
                (s: any) =>
                  s.supplierId === partialPaymentModal.supplierId &&
                  (s.locationId?.toString() || null) ===
                    (partialPaymentModal.locationId?.toString() || null),
              );
              if (supplier) {
                // Include daily revenue in total
                const dailyAdded = getDailyAdded(
                  partialPaymentModal.categoryId,
                  supplier,
                );
                currentTotal = (supplier.totalValue || 0) + dailyAdded;
              }
            }
          }

          return (
            <>
              <div style={{ marginBottom: 16 }}>
                <Text type="secondary">
                  {t("Total amount")}:{" "}
                  <strong>{currentTotal.toLocaleString("sr-RS")} RSD</strong>
                </Text>
              </div>

              <div style={{ marginBottom: 16 }}>
                <Text strong style={{ display: "block", marginBottom: 8 }}>
                  {t("Paid amount")}
                </Text>
                <InputNumber
                  min={0}
                  max={currentTotal}
                  value={partialPaymentModal.paidAmount}
                  onChange={(value) => {
                    const paid = Math.max(
                      0,
                      Math.min(currentTotal, Number(value || 0)),
                    );
                    const remaining = currentTotal - paid;
                    setPartialPaymentModal((prev) => ({
                      ...prev,
                      paidAmount: paid,
                      remainingAmount: Math.max(0, remaining),
                    }));
                  }}
                  style={{ width: "100%" }}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value?.replace(/,/g, "") as any}
                  suffix="RSD"
                />
              </div>

              <div style={{ marginBottom: 16 }}>
                <Text strong style={{ display: "block", marginBottom: 8 }}>
                  {t("Remaining amount")}
                </Text>
                <InputNumber
                  min={0}
                  max={currentTotal}
                  value={partialPaymentModal.remainingAmount}
                  onChange={(value) => {
                    const remaining = Math.max(
                      0,
                      Math.min(currentTotal, Number(value || 0)),
                    );
                    const paid = currentTotal - remaining;
                    setPartialPaymentModal((prev) => ({
                      ...prev,
                      paidAmount: Math.max(0, paid),
                      remainingAmount: remaining,
                    }));
                  }}
                  style={{ width: "100%" }}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value?.replace(/,/g, "") as any}
                  suffix="RSD"
                />
              </div>

              <Divider />

              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <Text type="secondary">{t("Sum check")}:</Text>
                <Text
                  strong
                  style={{
                    color:
                      Math.abs(
                        partialPaymentModal.paidAmount +
                          partialPaymentModal.remainingAmount -
                          currentTotal,
                      ) < 0.01
                        ? "#52c41a"
                        : "#ff4d4f",
                  }}
                >
                  {(
                    partialPaymentModal.paidAmount +
                    partialPaymentModal.remainingAmount
                  ).toLocaleString("sr-RS")}{" "}
                  RSD
                </Text>
              </div>
            </>
          );
        })()}
      </Modal>

      {/* CUSTOM ENTRY PARTIAL PAYMENT MODAL */}
      <Modal
        title={t("Partial Payment Details - Custom Entry")}
        open={customEntryPartialModal.visible}
        onOk={handleCustomEntryPartialPaymentConfirm}
        className="standardHeader standardFooter"
        onCancel={handleCustomEntryPartialPaymentCancel}
        okText={t("Confirm")}
        cancelText={t("Cancel")}
        width={500}
      >
        {(() => {
          const currentTotal = customEntryPartialModal.totalAmount;

          return (
            <>
              <div style={{ marginBottom: 16 }}>
                <Text type="secondary">
                  {t("Total amount")}:{" "}
                  <strong>{currentTotal.toLocaleString("sr-RS")} RSD</strong>
                </Text>
              </div>

              <div style={{ marginBottom: 16 }}>
                <Text strong style={{ display: "block", marginBottom: 8 }}>
                  {t("Paid amount")}
                </Text>
                <InputNumber
                  min={0}
                  max={currentTotal}
                  value={customEntryPartialModal.paidAmount}
                  onChange={(value) => {
                    const paid = Math.max(
                      0,
                      Math.min(currentTotal, Number(value || 0)),
                    );
                    const remaining = currentTotal - paid;
                    setCustomEntryPartialModal((prev) => ({
                      ...prev,
                      paidAmount: paid,
                      remainingAmount: Math.max(0, remaining),
                    }));
                  }}
                  style={{ width: "100%" }}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value?.replace(/,/g, "") as any}
                  suffix="RSD"
                />
              </div>

              <div style={{ marginBottom: 16 }}>
                <Text strong style={{ display: "block", marginBottom: 8 }}>
                  {t("Remaining amount")}
                </Text>
                <InputNumber
                  min={0}
                  max={currentTotal}
                  value={customEntryPartialModal.remainingAmount}
                  onChange={(value) => {
                    const remaining = Math.max(
                      0,
                      Math.min(currentTotal, Number(value || 0)),
                    );
                    const paid = currentTotal - remaining;
                    setCustomEntryPartialModal((prev) => ({
                      ...prev,
                      paidAmount: Math.max(0, paid),
                      remainingAmount: remaining,
                    }));
                  }}
                  style={{ width: "100%" }}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value?.replace(/,/g, "") as any}
                  suffix="RSD"
                />
              </div>

              <Divider />

              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <Text type="secondary">{t("Sum check")}:</Text>
                <Text
                  strong
                  style={{
                    color:
                      Math.abs(
                        customEntryPartialModal.paidAmount +
                          customEntryPartialModal.remainingAmount -
                          currentTotal,
                      ) < 0.01
                        ? "#52c41a"
                        : "#ff4d4f",
                  }}
                >
                  {(
                    customEntryPartialModal.paidAmount +
                    customEntryPartialModal.remainingAmount
                  ).toLocaleString("sr-RS")}{" "}
                  RSD
                </Text>
              </div>
            </>
          );
        })()}
      </Modal>

      {deductionsModalVisible && (
        <DeductionsDetailsModal
          visible={deductionsModalVisible}
          onClose={() => setDeductionsModalVisible(false)}
          t={t}
          matrix={matrix}
          locations={locations}
          locationFilters={locationFilters}
          dailyRevenueDeductions={dailyRevenueDeductions}
          getDisplayedTotal={getDisplayedTotal}
          getDisplayedCustom={getDisplayedCustom}
        />
      )}

      {/* Daily Revenue Entry Payment Status Modal */}
      <Modal
        title={t("expensesMatrix.editPaymentStatus")}
        open={dailyRevenuePaymentModal.visible}
        onOk={handleDailyRevenuePaymentSave}
        className="standardHeader standardFooter"
        onCancel={handleDailyRevenuePaymentCancel}
        okText={t("Save")}
        cancelText={t("Cancel")}
        width={500}
      >
        <div style={{ marginBottom: "16px" }}>
          <p style={{ marginBottom: "8px" }}>
            <strong>{t("Date")}:</strong>{" "}
            {dailyRevenuePaymentModal.date
              ? dayjs(dailyRevenuePaymentModal.date).format("DD.MM.YYYY")
              : ""}
          </p>
          <p style={{ fontWeight: 500, fontSize: "16px" }}>
            {t("Total amount")}:{" "}
            <strong>
              {dailyRevenuePaymentModal.totalAmount.toLocaleString("sr-RS")} RSD
            </strong>
          </p>
        </div>

        <Divider />

        {/* Payment Status Selection */}
        <div style={{ marginBottom: "24px" }}>
          <Text strong style={{ display: "block", marginBottom: "12px" }}>
            {t("expensesMatrix.paymentStatus")}:
          </Text>
          <div style={{ display: "flex", gap: "12px" }}>
            <div
              onClick={() => handleDailyRevenuePaymentStatusChange("paid")}
              style={{
                flex: 1,
                padding: "12px",
                borderRadius: "8px",
                border:
                  dailyRevenuePaymentModal.currentStatus === "paid"
                    ? "2px solid #52c41a"
                    : "2px solid #d9d9d9",
                backgroundColor:
                  dailyRevenuePaymentModal.currentStatus === "paid"
                    ? "#f6ffed"
                    : "#fff",
                cursor: "pointer",
                textAlign: "center",
                transition: "all 0.3s",
              }}
            >
              <div
                style={{
                  width: "24px",
                  height: "24px",
                  borderRadius: "50%",
                  backgroundColor: "#52c41a",
                  margin: "0 auto 8px",
                }}
              />
              <Text strong>{t("expensesMatrix.paid")}</Text>
            </div>

            <div
              onClick={() => handleDailyRevenuePaymentStatusChange("unpaid")}
              style={{
                flex: 1,
                padding: "12px",
                borderRadius: "8px",
                border:
                  dailyRevenuePaymentModal.currentStatus === "unpaid"
                    ? "2px solid #ff4d4f"
                    : "2px solid #d9d9d9",
                backgroundColor:
                  dailyRevenuePaymentModal.currentStatus === "unpaid"
                    ? "#fff1f0"
                    : "#fff",
                cursor: "pointer",
                textAlign: "center",
                transition: "all 0.3s",
              }}
            >
              <div
                style={{
                  width: "24px",
                  height: "24px",
                  borderRadius: "50%",
                  backgroundColor: "#ff4d4f",
                  margin: "0 auto 8px",
                }}
              />
              <Text strong>{t("expensesMatrix.unpaid")}</Text>
            </div>

            <div
              onClick={() => handleDailyRevenuePaymentStatusChange("partial")}
              style={{
                flex: 1,
                padding: "12px",
                borderRadius: "8px",
                border:
                  dailyRevenuePaymentModal.currentStatus === "partial"
                    ? "2px solid #faad14"
                    : "2px solid #d9d9d9",
                backgroundColor:
                  dailyRevenuePaymentModal.currentStatus === "partial"
                    ? "#fffbe6"
                    : "#fff",
                cursor: "pointer",
                textAlign: "center",
                transition: "all 0.3s",
              }}
            >
              <div
                style={{
                  width: "24px",
                  height: "24px",
                  borderRadius: "50%",
                  backgroundColor: "#faad14",
                  margin: "0 auto 8px",
                }}
              />
              <Text strong>{t("expensesMatrix.partial")}</Text>
            </div>
          </div>
        </div>

        {/* Partial Payment Details */}
        {dailyRevenuePaymentModal.currentStatus === "partial" && (
          <>
            <Divider />
            <div style={{ marginBottom: "16px" }}>
              <Text strong style={{ display: "block", marginBottom: "8px" }}>
                {t("Paid amount")}:
              </Text>
              <InputNumber
                min={0}
                max={dailyRevenuePaymentModal.totalAmount}
                value={dailyRevenuePaymentModal.paidAmount}
                onChange={(value) => {
                  const paid = Math.max(
                    0,
                    Math.min(
                      dailyRevenuePaymentModal.totalAmount,
                      Number(value || 0),
                    ),
                  );
                  const remaining = dailyRevenuePaymentModal.totalAmount - paid;
                  setDailyRevenuePaymentModal((prev) => ({
                    ...prev,
                    paidAmount: paid,
                    remainingAmount: Math.max(0, remaining),
                  }));
                }}
                style={{ width: "100%" }}
                formatter={(value) =>
                  `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                }
                parser={(value) => value?.replace(/,/g, "") as any}
                suffix="RSD"
              />
            </div>

            <div style={{ marginBottom: "16px" }}>
              <Text strong style={{ display: "block", marginBottom: "8px" }}>
                {t("Remaining amount")}:
              </Text>
              <InputNumber
                min={0}
                max={dailyRevenuePaymentModal.totalAmount}
                value={dailyRevenuePaymentModal.remainingAmount}
                onChange={(value) => {
                  const remaining = Math.max(
                    0,
                    Math.min(
                      dailyRevenuePaymentModal.totalAmount,
                      Number(value || 0),
                    ),
                  );
                  const paid = dailyRevenuePaymentModal.totalAmount - remaining;
                  setDailyRevenuePaymentModal((prev) => ({
                    ...prev,
                    paidAmount: Math.max(0, paid),
                    remainingAmount: remaining,
                  }));
                }}
                style={{ width: "100%" }}
                formatter={(value) =>
                  `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                }
                parser={(value) => value?.replace(/,/g, "") as any}
                suffix="RSD"
              />
            </div>

            <Divider />

            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <Text type="secondary">{t("Sum check")}:</Text>
              <Text
                strong
                style={{
                  color:
                    Math.abs(
                      dailyRevenuePaymentModal.paidAmount +
                        dailyRevenuePaymentModal.remainingAmount -
                        dailyRevenuePaymentModal.totalAmount,
                    ) < 0.01
                      ? "#52c41a"
                      : "#ff4d4f",
                }}
              >
                {(
                  dailyRevenuePaymentModal.paidAmount +
                  dailyRevenuePaymentModal.remainingAmount
                ).toLocaleString("sr-RS")}{" "}
                RSD
              </Text>
            </div>
          </>
        )}
      </Modal>

      {/* Unpaid Expenses Modal */}
      <UnpaidExpensesModal
        visible={unpaidExpensesModalVisible}
        onClose={() => setUnpaidExpensesModalVisible(false)}
        matrix={matrix}
        locationFilters={locationFilters}
        locationMap={locationMap}
        onCustomEntryStatusChange={(item, newStatus) => {
          if (newStatus === "partial") {
            // Open partial payment modal for custom entries
            setCustomEntryPartialModal({
              visible: true,
              categoryId: item.categoryId,
              supplierId: item.supplierId,
              locationId: item.locationId,
              entryIndex: item.entryIndex,
              totalAmount: item.amount,
              paidAmount: item.paidAmount,
              remainingAmount: item.remainingAmount,
            });
            setUnpaidExpensesModalVisible(false);
          } else {
            // Directly update status via API for paid/unpaid
            handleQuickCustomEntryStatusChange(item, newStatus);
          }
        }}
        onDailyRevenueStatusChange={(item, newStatus) => {
          if (newStatus === "partial") {
            // Open partial payment modal for daily revenue
            setDailyRevenuePaymentModal({
              visible: true,
              entryId: null,
              categoryId: item.categoryId,
              supplierId: item.supplierId,
              locationId: item.locationId,
              date: item.date,
              totalAmount: item.amount,
              currentStatus: newStatus,
              paidAmount: item.paidAmount,
              remainingAmount: item.remainingAmount,
            });
            setUnpaidExpensesModalVisible(false);
          } else {
            // Directly update status via API for paid/unpaid
            handleQuickDailyRevenueStatusChange(item, newStatus);
          }
        }}
        onDefaultValueStatusChange={(item, newStatus) => {
          if (newStatus === "partial") {
            // Open partial payment modal for default value
            setCustomEntryPartialModal({
              visible: true,
              categoryId: item.categoryId,
              supplierId: item.supplierId,
              locationId: item.locationId,
              entryIndex: -1, // -1 indicates default value
              totalAmount: item.amount,
              paidAmount: item.paidAmount,
              remainingAmount: item.remainingAmount,
            });
            setUnpaidExpensesModalVisible(false);
          } else {
            // Directly update status via API for paid/unpaid
            handleQuickDefaultValueStatusChange(item, newStatus);
          }
        }}
      />

      {/* Custom Value Modal */}
      <Modal
        title={t("Add Custom Value")}
        open={customValueModalVisible}
        onOk={handleAddCustomValue}
        onCancel={() => {
          setCustomValueModalVisible(false);
          setNewCustomValue({
            description: "",
            amount: 0,
            currency: "RSD",
            month: dayjs().month() + 1,
            year: dayjs().year(),
            invoiceDate: dayjs().format("YYYY-MM-DD"),
          });
          setSelectedSupplierForCustomValue(null);
        }}
        okText={t("Add")}
        cancelText={t("Cancel")}
        className="standardHeader standardFooter"
      >
        {selectedSupplierForCustomValue && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <Text strong>{t("Supplier")}:</Text>
              <div>
                <Text>{selectedSupplierForCustomValue.supplierName}</Text>
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <Text strong>{t("Location")}:</Text>
              <div>
                <Text>
                  {
                    availableLocations.find(
                      (loc: any) =>
                        loc._id === selectedSupplierForCustomValue.locationId,
                    )?.name
                  }
                </Text>
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <Text strong>{t("Month/Year")}:</Text>
              <DatePicker
                picker="month"
                value={dayjs()
                  .year(newCustomValue.year)
                  .month(newCustomValue.month - 1)}
                onChange={(date) => {
                  if (date) {
                    setNewCustomValue({
                      ...newCustomValue,
                      month: date.month() + 1,
                      year: date.year(),
                      // Reset invoice date to first day of new month
                      invoiceDate: date.startOf("month").format("YYYY-MM-DD"),
                    });
                  }
                }}
                format="MMMM YYYY"
                style={{ width: "100%", marginTop: 8 }}
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <Text strong style={{ display: "block", marginBottom: 8 }}>
                {t("Invoice Date")}
              </Text>
              <DatePicker
                value={dayjs(newCustomValue.invoiceDate)}
                onChange={(date) => {
                  if (date) {
                    // Ensure date is within selected month
                    if (
                      date.month() + 1 === newCustomValue.month &&
                      date.year() === newCustomValue.year
                    ) {
                      setNewCustomValue({
                        ...newCustomValue,
                        invoiceDate: date.format("YYYY-MM-DD"),
                      });
                    } else {
                      message.warning(
                        t("Invoice date must be within selected month"),
                      );
                    }
                  }
                }}
                format="DD.MM.YYYY"
                style={{ width: "100%" }}
                disabledDate={(current) => {
                  if (!current) return false;
                  // Disable dates outside selected month/year
                  return (
                    current.month() + 1 !== newCustomValue.month ||
                    current.year() !== newCustomValue.year
                  );
                }}
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <Text strong style={{ display: "block", marginBottom: 8 }}>
                {t("Description")}
              </Text>
              <Input
                placeholder={t("Enter description")}
                value={newCustomValue.description}
                onChange={(e) =>
                  setNewCustomValue((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <Text strong style={{ display: "block", marginBottom: 8 }}>
                {t("Currency")}
              </Text>
              <Select
                value={newCustomValue.currency}
                onChange={(value) =>
                  setNewCustomValue((prev) => ({
                    ...prev,
                    currency: value,
                  }))
                }
                style={{ width: "100%" }}
              >
                <Option value="RSD">RSD</Option>
                <Option value="EUR">EUR</Option>
              </Select>
            </div>

            <div style={{ marginBottom: 16 }}>
              <Text strong style={{ display: "block", marginBottom: 8 }}>
                {t("Amount")}
              </Text>
              <InputNumber
                min={0}
                placeholder="0"
                value={newCustomValue.amount}
                onChange={(value) =>
                  setNewCustomValue((prev) => ({
                    ...prev,
                    amount: Number(value || 0),
                  }))
                }
                style={{ width: "100%" }}
                formatter={(value) =>
                  `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                }
                parser={(value) => value?.replace(/,/g, "") as any}
              />
            </div>
          </div>
        )}
      </Modal>

      <ManageExtraIncomeModal
        visible={isExtraIncomeModalOpen}
        onClose={() => setIsExtraIncomeModalOpen(false)}
        dateRange={dateRange}
        locationFilters={locationFilters}
        onUpdate={() => {
          if (dateRange[0] && dateRange[1]) {
            fetchExtraIncome(dateRange[0], dateRange[1]);
          }
        }}
      />
    </div>
  );
};

export default ExpenseMatrixPage;
